/* ================================================================
   BRITTEN CUSTOM: The Picture Desk — behaviour
   Location: assets/britten-picture-desk.js
   Called from: snippets/britten-picture-desk.liquid
   See that snippet's documentation block for the full brief.

   Sections: data load, image URLs, packer (grid layout), hue sort +
   spectrum, tile render + FLIP resort, lightbox (URL state, keys,
   swipe), opening rotation, boot.

   Batch rendering — the archive can be ~1,900 photos. Only INITIAL_BATCH
   (48) are ever packed/rendered up front; ensurePacked() is append-only
   (a persistent occupancy grid + row pointer per order/filter/column
   state) so growing the batch only ever costs the new slice, never a full
   re-pack. A sentinel element below the grid (.bpd-sentinel) grows
   state.renderCount by BATCH_SIZE as the visitor nears it. A resort/
   filter change resets renderCount back to INITIAL_BATCH (see
   lastFilterKey in renderGrid) — cheap because it's bounded, not because
   it skips work. This replaced a single synchronous render of the whole
   archive the moment the full JSON arrived, which froze the page for
   ~2s on a throttled mobile CPU.
   ================================================================ */
(function () {
  'use strict';

  // === CONFIGURATION ===
  var FEATURE_ENABLED = true;
  var DEBUG_MODE = false;
  // "Email me my edit" is built (markup + wiring) but not switched on —
  // Klaviyo isn't wired yet. Flip this when it is.
  var EMAIL_ENABLED = false;
  // "Your edit" (favourites) is built but not ready for owner review yet —
  // this single switch hides the pill, the lightbox add/remove button,
  // long-press-to-add, the tile in-edit outline, the shared ?edit= link
  // flow and the "Keep an edit" how-it-works step. Flip back on when ready;
  // the markup for the step and the tray must also come back in the
  // snippet (see britten-picture-desk.liquid).
  var EDIT_ENABLED = false;
  var EDIT_STORAGE_KEY = 'britten.pictureDesk.edit';
  var EDIT_MAX = 12;
  // Tiles are rendered in batches, not all 1,892 at once — the first
  // batch renders immediately, further batches append as the visitor
  // scrolls near the end (see the sentinel wiring near renderGrid).
  var INITIAL_BATCH = 48;
  var BATCH_SIZE = 48;

  function log(msg) {
    if (DEBUG_MODE) console.log('[Britten Picture Desk] ' + msg);
  }

  if (!FEATURE_ENABLED) return;

  var root = document.querySelector('.bpd');
  if (!root) return;

  // ---------------------------------------------------------------
  // Landing mode — the wall embedded on a colour/setting/season/country
  // landing page (templates/page.wedding-landing.liquid renders this
  // snippet with `mode: 'landing', landing: page.handle`), locked to one
  // landing rather than the full archive. See the snippet's own header
  // for the markup contract (`data-bpd-mode` / `data-bpd-landing`).
  // ---------------------------------------------------------------
  var MODE = root.getAttribute('data-bpd-mode') || 'archive';
  var LANDING_MODE = MODE === 'landing';
  var LOCKED_LANDING = root.getAttribute('data-bpd-landing') || null;

  // ---------------------------------------------------------------
  // GA4 — PRIMARY path is window.Shopify.analytics.publish: GA4 on this
  // store runs inside Shopify's web-pixel sandbox, so window.gtag/
  // window.dataLayer do not exist on the live page and calling either
  // goes nowhere. A custom pixel in admin (see docs/veil-guide-
  // custom-pixel.md) subscribes to these britten_ event names and
  // forwards them to GA4. gtag/dataLayer are kept as a harmless SECONDARY
  // path only (free if a future embed ever exposes them directly).
  // Payload is kept flat (strings/numbers only). Analytics must never
  // break the page: every call is try/caught.
  // ---------------------------------------------------------------
  var EVENT_PREFIX = 'britten_';

  function track(name, data) {
    var eventName = EVENT_PREFIX + name;
    var payload = data || {};
    // Every event carries landing context while the wall is locked to one
    // (spec: "existing britten_wall_* events should carry context:'landing'
    // and landing:{handle} when on a landing page").
    if (LANDING_MODE && LOCKED_LANDING) {
      payload.context = 'landing';
      payload.landing = LOCKED_LANDING;
    }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch (e) { /* analytics must never break the page */ }
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: eventName, britten: payload });
      }
    } catch (e) { /* analytics must never break the page */ }
    log(eventName + ' ' + JSON.stringify(payload));
  }

  var REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------------------------------------------------------------
  // State
  // ---------------------------------------------------------------
  var state = {
    photos: [],       // all photos loaded so far, in rank order
    order: 'rank',    // 'rank' | 'hue'
    hue: null,        // number 0-360, 'neutral' (legacy generic), or 'neutral-<id>'
    lens: null,       // lens id, or null
    query: '',        // search text, lowercased
    landing: null,    // landing handle, or null — ?landing=<handle>
    landings: [],     // [{h,t,k,v,n,hue}] from the data, top-level `landings`
    lightboxId: null,
    lightboxOpener: null, // element to refocus when the lightbox closes
    fullLoaded: false,
    hasLenses: false, // true once any loaded photo carries an `l` array
    renderCount: INITIAL_BATCH, // how many of the ordered/filtered list are materialised in the DOM
    // Colour landing only (see computeShadeStops): the page's own 4-6
    // light-to-dark shade stops, or null on every other page/landing kind
    // (setting/season/country landings and the plain archive keep the
    // normal full hue spectrum). When set, `state.hue` holds a
    // 'shade:<index>' key into this array rather than a numeric hue.
    shadeStops: null
  };
  var LENS_LABELS = {
    portraits: 'Portraits',
    veil: 'The veil',
    details: 'Details',
    flowers: 'Flowers',
    ceremony: 'Ceremony',
    cake: 'Cake and table',
    dancing: 'Dancing'
  };
  var LENS_ORDER = ['portraits', 'veil', 'details', 'flowers', 'ceremony', 'cake', 'dancing'];
  var tileEls = {};   // id -> tile element
  var byId = {};       // id -> photo

  // ---------------------------------------------------------------
  // Your edit — favourites, persisted in localStorage
  // ---------------------------------------------------------------
  var editIds = [];     // ordered array of ids, source of truth
  var editSet = {};     // id -> true, for fast lookup
  var sharedIds = null; // ids from an incoming ?edit= link, not yet merged

  function loadEdit() {
    try {
      var raw = window.localStorage.getItem(EDIT_STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(function (id) { return typeof id === 'string'; }) : [];
    } catch (e) {
      log('could not read edit from storage: ' + e);
      return [];
    }
  }

  function saveEdit() {
    try {
      window.localStorage.setItem(EDIT_STORAGE_KEY, JSON.stringify(editIds));
    } catch (e) {
      log('could not save edit to storage: ' + e);
    }
  }

  function rebuildEditSet() {
    editSet = {};
    editIds.forEach(function (id) { editSet[id] = true; });
  }

  function isInEdit(id) { return !!editSet[id]; }

  function addToEdit(id) {
    if (editSet[id]) return true;
    if (editIds.length >= EDIT_MAX) {
      showEditNotice('full');
      return false;
    }
    editIds.push(id);
    rebuildEditSet();
    saveEdit();
    onEditChanged();
    track('wall_edit_add', { photo_id: id });
    return true;
  }

  function removeFromEdit(id) {
    if (!editSet[id]) return;
    editIds = editIds.filter(function (i) { return i !== id; });
    rebuildEditSet();
    saveEdit();
    onEditChanged();
    track('wall_edit_remove', { photo_id: id });
  }

  function toggleEdit(id) {
    if (isInEdit(id)) removeFromEdit(id);
    else addToEdit(id);
  }

  function resetEdit() {
    editIds = [];
    rebuildEditSet();
    saveEdit();
    onEditChanged();
  }

  // ---------------------------------------------------------------
  // Image URLs
  // ---------------------------------------------------------------
  var WIDTHS = { tile1: 700, tile2: 1000, hero: 1800 };

  function cdnUrl(u, w) {
    var sep = u.indexOf('?') > -1 ? '&' : '?';
    return u + sep + 'width=' + w;
  }

  // Every photo is on cdn.shopify.com now — including the ones copied over
  // from Yotpo, which had no `?width=` support at their old host. Decide
  // by the URL's actual host at runtime rather than trusting `photo.cdn`
  // (which could go stale if a future ingest forgets to set it) — a
  // Shopify CDN URL always accepts `&width=`.
  function isShopifyCdn(u) {
    return /^https?:\/\/cdn\.shopify\.com\//.test(u);
  }

  function buildImgAttrs(photo, kind) {
    var w = WIDTHS[kind] || 700;
    if (photo.cdn || isShopifyCdn(photo.u)) {
      return { src: cdnUrl(photo.u, w), srcset: cdnUrl(photo.u, w) + ' 1x, ' + cdnUrl(photo.u, w * 2) + ' 2x' };
    }
    return { src: photo.u, srcset: '' };
  }

  // ---------------------------------------------------------------
  // Colour: hue / saturation / lightness from hex, hue distance,
  // spectrum stops. Ported from the design reference's tiles()/
  // hueDist()/spectrumStops() in Moments Wall.dc.html.
  // ---------------------------------------------------------------
  function hsl(hex) {
    if (!hex) return { h: 0, s: 0, l: 0.5 };
    var n = parseInt(hex.replace('#', ''), 16);
    var r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
    var h = 0;
    if (d) {
      if (mx === r) h = ((g - b) / d + (g < b ? 6 : 0));
      else if (mx === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60;
    }
    var l = (mx + mn) / 2;
    var s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
    return { h: h, s: s, l: l };
  }

  // Inverse of hsl() above — used to generate the light-to-dark shade ramp
  // for a colour landing with no real palette yet (see computeShadeStops).
  function hslToHex(h, s, l) {
    h = ((h % 360) + 360) % 360;
    var c = (1 - Math.abs(2 * l - 1)) * s;
    var x = c * (1 - Math.abs((h / 60) % 2 - 1));
    var m = l - c / 2;
    var r, g, b;
    if (h < 60) { r = c; g = x; b = 0; }
    else if (h < 120) { r = x; g = c; b = 0; }
    else if (h < 180) { r = 0; g = c; b = x; }
    else if (h < 240) { r = 0; g = x; b = c; }
    else if (h < 300) { r = x; g = 0; b = c; }
    else { r = c; g = 0; b = x; }
    function ch(v) { return Math.round((v + m) * 255).toString(16).padStart(2, '0'); }
    return '#' + ch(r) + ch(g) + ch(b);
  }

  function photoHsl(p) {
    if (!p._hsl) p._hsl = hsl(p.c);
    return p._hsl;
  }

  // Colour landing shade track (see computeShadeStops, renderSpectrum) —
  // `state.hue` becomes a 'shade:<index>' string rather than a plain hue
  // number while a landing's own shade stops are active. isShadeKey/
  // shadeStopFor let hueDist/listDist below (and positionThumb/
  // pickFromPointer, which just pass state.hue straight through) treat it
  // transparently: same distance-sort machinery, one extra branch.
  function isShadeKey(hue) {
    return typeof hue === 'string' && hue.indexOf('shade:') === 0;
  }
  function shadeStopFor(hue) {
    if (!isShadeKey(hue) || !state.shadeStops) return null;
    return state.shadeStops[parseInt(hue.slice(6), 10)] || null;
  }
  // Distance to a specific shade stop: hue proximity (photos are already
  // filtered to this landing's colour, so hue does most of the work) plus
  // lightness proximity (so the ramp genuinely orders light to dark /
  // pale to deep, rather than just clustering on hue alone).
  function shadeDist(c, shade) {
    var dh = Math.abs(c.h - shade.h);
    dh = Math.min(dh, 360 - dh);
    var dl = Math.abs(c.l - shade.l) * 200;
    return dh * 0.5 + dl;
  }

  // Whole-photo pixel distance (dominant colour `c`) — the original
  // behaviour, kept as the fallback for photos with no styling colours
  // (`sc`) at all. See colourSortInfo() for when this is used.
  function hueDist(p, hue) {
    var c = photoHsl(p);
    var shade = shadeStopFor(hue);
    if (shade) return shadeDist(c, shade);
    if (isNeutralHue(hue)) return c.s * 400 + Math.abs(c.l - neutralTargetL(hue)) * 20;
    if (c.s < 0.1) return 400 + c.s * 10;
    var d = Math.abs(c.h - hue);
    return Math.min(d, 360 - d);
  }

  // ---------------------------------------------------------------
  // Element colours (`ec`) and styling colours (`sc`) — per-element
  // hex lists so a colour sort matches the actual flowers/attire/decor,
  // not a whole-photo pixel average (a navy suit shouldn't rank a photo
  // as "blue" when the lens is Flowers). Parsed to {h,s,l} once per
  // photo on ingest (ensureColourCache), never re-parsed on resort —
  // only the distance-to-selected-hue is recomputed each time, and
  // that's one pass over the already-filtered list, same shape as the
  // old pixel-only sort.
  // ---------------------------------------------------------------
  function parseHexList(list) {
    return (list && list.length) ? list.map(hsl) : null;
  }

  function ensureColourCache(p) {
    if (p._ccParsed) return;
    p._ccParsed = true;
    var ec = p.ec || {};
    p._ecF = parseHexList(ec.f);
    p._ecB = parseHexList(ec.b);
    p._ecA = parseHexList(ec.a);
    p._ecD = parseHexList(ec.d);
    p._ecK = parseHexList(ec.k);
    p._sc = parseHexList(p.sc);
    // The scene's own colours (whole-photo pixel swatches) minus the
    // ivory/white of the veil and dress — used for The veil lens, where
    // the veil is always ivory and the setting carries the colour.
    var bg = (p.sw || []).map(function (s) { return hsl(s[0]); })
      .filter(function (c) { return !(c.l > 0.8 && c.s < 0.3); });
    p._bg = bg.length ? bg : null;
  }

  function concatLists() {
    var out = null;
    for (var i = 0; i < arguments.length; i++) {
      var l = arguments[i];
      if (l && l.length) out = out ? out.concat(l) : l.slice();
    }
    return out;
  }

  // Which parsed hex list is relevant for the current lens (README
  // colour rules): Flowers → ec.f; Details is close-ups, so it's ec.f +
  // ec.d + ec.k (whatever's there); Cake → ec.k + ec.d; every other lens
  // (and no lens at all) → the styling colours union, sc.
  function relevantHslList(p, lens) {
    if (lens === 'flowers') return p._ecF;
    if (lens === 'details') return concatLists(p._ecF, p._ecD, p._ecK);
    if (lens === 'cake') return concatLists(p._ecK, p._ecD);
    if (lens === 'veil') return p._bg;
    return p._sc;
  }

  // Min hue/lightness distance between `hue` and any hex in the list.
  function listDist(list, hue) {
    var shade = shadeStopFor(hue);
    var min = Infinity;
    for (var i = 0; i < list.length; i++) {
      var c = list[i], d;
      if (shade) d = shadeDist(c, shade);
      else if (isNeutralHue(hue)) d = c.s * 400 + Math.abs(c.l - neutralTargetL(hue)) * 20;
      else if (c.s < 0.1) d = 400 + c.s * 10;
      else { var dd = Math.abs(c.h - hue); d = Math.min(dd, 360 - dd); }
      if (d < min) min = d;
    }
    return min;
  }

  // tier 0 = photo has the element list the current lens cares about —
  // sorted by real distance. tier 1 = it doesn't (e.g. lens Flowers but
  // no ec.f) — sorts to the end, never removed. Within tier 1, a photo
  // with no `sc` at all falls back to the old pixel distance so it still
  // has *some* order, but always after every tier-0 photo.
  function colourSortInfo(p, hue, lens) {
    var list = relevantHslList(p, lens);
    if (list) return { tier: 0, d: listDist(list, hue) };
    if (lens === 'flowers' || lens === 'details' || lens === 'cake') {
      return { tier: 1, d: Infinity };
    }
    return { tier: 1, d: hueDist(p, hue) };
  }

  // Six neutral swatches, lightest to darkest. Each gets its own name and
  // lightness target so ranking by "dark" no longer collapses onto the same
  // leading photos as "ivory" (audit: every neutral swatch set
  // `colour=neutral` and sorted by distance to the same mid-grey target,
  // regardless of which was actually tapped). Bare `colour=neutral` (no
  // suffix) keeps working, as an alias for that old, generic mid-grey
  // target — existing shared/bookmarked links still filter sensibly.
  var NEUTRAL_STOPS = [
    { c: '#FDFCFA', id: 'ivory' },
    { c: '#e9e6db', id: 'linen' },
    { c: '#c4b6a6', id: 'taupe' },
    { c: '#8a8078', id: 'stone' },
    { c: '#3d3730', id: 'espresso' },
    { c: '#141210', id: 'dark' }
  ];
  var NEUTRAL_TARGET_L = { 'neutral': 0.5 };
  NEUTRAL_STOPS.forEach(function (stop) {
    NEUTRAL_TARGET_L['neutral-' + stop.id] = hsl(stop.c).l;
  });

  function isNeutralHue(hue) {
    return hue === 'neutral' || (typeof hue === 'string' && hue.indexOf('neutral-') === 0);
  }
  function neutralTargetL(hue) {
    var l = NEUTRAL_TARGET_L[hue];
    return l === undefined ? 0.5 : l;
  }
  // Closest named neutral stop to an arbitrary near-neutral lightness (a
  // tile swatch tap or a mood-board colour match, not the spectrum itself).
  function nearestNeutralHue(l) {
    var bestId = NEUTRAL_STOPS[0].id, bestD = Infinity;
    NEUTRAL_STOPS.forEach(function (stop) {
      var d = Math.abs(NEUTRAL_TARGET_L['neutral-' + stop.id] - l);
      if (d < bestD) { bestD = d; bestId = stop.id; }
    });
    return 'neutral-' + bestId;
  }

  // Quantised into ~20 equal hue buckets (mean colour per bucket) — a
  // handful of solid blocks the eye can read, not hundreds of noisy
  // slivers. Real per-photo sorting still uses continuous hue distance
  // (hueDist); these buckets are only the drag target / visual bar.
  var HUE_BUCKET_COUNT = 20;

  function meanHex(hexList) {
    var r = 0, g = 0, b = 0;
    hexList.forEach(function (hex) {
      var n = parseInt(hex.replace('#', ''), 16);
      r += (n >> 16) & 255; g += (n >> 8) & 255; b += n & 255;
    });
    var n = hexList.length;
    function hex2(v) { return Math.round(v / n).toString(16).padStart(2, '0'); }
    return '#' + hex2(r) + hex2(g) + hex2(b);
  }

  // Colour landings (mode:'landing', k:'colour') replace the full hue
  // spectrum with the page's own shade strip — a full rainbow track on a
  // blush-pink page reads as noise, and the thumb had nowhere on it that
  // actually meant "blush". Prefers a real palette (`landing.pal`,
  // [{hex,name}], light→dark, 4-6 stops); falls back to a generated
  // 6-step ramp around the landing's hue (real `landing.hue` or
  // LANDING_HUE_FALLBACK) at fixed saturation, lightness 85% down to 35%.
  // Setting/season/country landings (no `pal`, and not `k:'colour'`) and
  // the plain archive are untouched — spectrumStops() below only calls
  // this for a locked colour landing.
  var SHADE_RAMP_STEPS = 6, SHADE_RAMP_SAT = 0.38, SHADE_RAMP_L_START = 0.85, SHADE_RAMP_L_END = 0.35;
  function computeShadeStops(landing) {
    if (!landing || landing.k !== 'colour') return null;
    if (landing.pal && landing.pal.length) {
      return landing.pal.map(function (p) {
        var c = hsl(p.hex);
        return { c: p.hex, name: p.name || null, h: c.h, l: c.l };
      });
    }
    var hue = (typeof landing.hue !== 'undefined' && landing.hue !== null) ? landing.hue : LANDING_HUE_FALLBACK[landing.h];
    if (typeof hue === 'undefined') return null;
    var stops = [];
    for (var i = 0; i < SHADE_RAMP_STEPS; i++) {
      var t = i / (SHADE_RAMP_STEPS - 1);
      var l = SHADE_RAMP_L_START + (SHADE_RAMP_L_END - SHADE_RAMP_L_START) * t;
      stops.push({ c: hslToHex(hue, SHADE_RAMP_SAT, l), name: null, h: hue, l: l });
    }
    return stops;
  }

  function spectrumStops() {
    if (LANDING_MODE && state.shadeStops) {
      return state.shadeStops.map(function (s, i) {
        return { c: s.c, hue: 'shade:' + i, name: s.name };
      });
    }
    // Prefer styling colours (`sc`) across the archive so the bar reflects
    // what's actually in the photos (flowers/attire/decor/cake), not a
    // whole-photo pixel average. Falls back to the old pixel behaviour
    // if no photo has `sc` yet (e.g. data not ingested/published yet).
    var hasSc = state.photos.some(function (p) { return p._sc && p._sc.length; });
    var entries = [];
    if (hasSc) {
      state.photos.forEach(function (p) {
        if (!p.sc || !p._sc) return;
        p.sc.forEach(function (hex, idx) {
          var c = p._sc[idx];
          if (c && c.s >= 0.1) entries.push({ hex: hex, h: c.h });
        });
      });
    } else {
      state.photos.forEach(function (p) {
        if (photoHsl(p).s >= 0.1) entries.push({ hex: p.c, h: photoHsl(p).h });
      });
    }
    var buckets = new Array(HUE_BUCKET_COUNT);
    entries.forEach(function (e) {
      var idx = Math.min(HUE_BUCKET_COUNT - 1, Math.floor((e.h / 360) * HUE_BUCKET_COUNT));
      (buckets[idx] || (buckets[idx] = [])).push(e.hex);
    });
    var stops = [];
    buckets.forEach(function (hexList, idx) {
      if (!hexList || !hexList.length) return;
      var hue = Math.round((idx + 0.5) * (360 / HUE_BUCKET_COUNT));
      stops.push({ c: meanHex(hexList), hue: hue });
    });
    NEUTRAL_STOPS.forEach(function (stop) { stops.push({ c: stop.c, hue: 'neutral-' + stop.id }); });
    return stops;
  }

  // ---------------------------------------------------------------
  // Packer — deterministic grid layout (README "Layout algorithm")
  // ---------------------------------------------------------------
  function cols() {
    var w = window.innerWidth;
    if (w < 600) return 2;
    if (w < 900) return 3;
    if (w < 1600) return 4;
    return 5;
  }

  // ---------------------------------------------------------------
  // Search — token-based filter over bride, venue, region, country,
  // photographer, product titles/short names, setting, season and
  // moments. The query is lowercased and split on whitespace, common
  // stopwords are dropped, and every remaining token must match
  // (AND) — either a small alias onto an exact facet value, or a
  // plain substring match against the photo's joined search text.
  // Combinable with colour and lens.
  // ---------------------------------------------------------------
  var SEARCH_STOPWORDS = {
    veil: 1, veils: 1, wedding: 1, weddings: 1, bride: 1, brides: 1,
    'in': 1, a: 1, an: 1, the: 1, 'with': 1, setting: 1, style: 1
  };

  // Exact-match on the facet itself, not the free-text blob — so e.g.
  // searching "barn" can't also pick up a venue whose name merely
  // contains "barn" (audit: "barn" must match the Barn category count).
  var SEARCH_ALIASES = {
    barn: function (p) { return p.st === 'barn'; },
    summer: function (p) { return p.sn === 'summer'; },
    church: function (p) { return p.st === 'church'; },
    flowers: function (p) { return !!(p.l && p.l.indexOf('flowers') > -1); }
  };

  function photoSearchText(p) {
    if (p._search) return p._search;
    var parts = [p.b, p.vn, p.rg, p.ct, p.ph];
    (p.pr || []).forEach(function (prod) { parts.push(prod.t); parts.push(prod.s); });
    if (p.st) parts.push(String(p.st).replace(/_/g, ' '));
    if (p.sn) parts.push(String(p.sn).replace(/_/g, ' '));
    (p.l || []).forEach(function (m) { parts.push(String(m).replace(/_/g, ' ')); });
    p._search = parts.filter(Boolean).join(' ').toLowerCase();
    return p._search;
  }

  function searchTokens(q) {
    return (q || '').toLowerCase().split(/\s+/).filter(function (t) {
      return t && !SEARCH_STOPWORDS[t];
    });
  }

  function matchesQueryTokens(p, tokens) {
    if (!tokens || !tokens.length) return true;
    var text = photoSearchText(p);
    return tokens.every(function (t) {
      var alias = SEARCH_ALIASES[t];
      return alias ? alias(p) : text.indexOf(t) > -1;
    });
  }

  function matchesQuery(p, q) {
    return matchesQueryTokens(p, searchTokens(q));
  }

  function matchesLens(p, lens) {
    if (!lens) return true;
    return !!(p.l && p.l.indexOf(lens) > -1);
  }

  function matchesLanding(p, handle) {
    if (!handle) return true;
    return !!(p.lp && p.lp.indexOf(handle) > -1);
  }

  function landingByHandle(handle) {
    if (!handle) return null;
    for (var i = 0; i < state.landings.length; i++) {
      if (state.landings[i].h === handle) return state.landings[i];
    }
    return null;
  }

  function filteredPhotos() {
    var tokens = searchTokens(state.query);
    var lens = state.lens;
    var landing = state.landing;
    if (!tokens.length && !lens && !landing) return state.photos;
    return state.photos.filter(function (p) {
      return matchesQueryTokens(p, tokens) && matchesLens(p, lens) && matchesLanding(p, landing);
    });
  }

  function orderedPhotos() {
    var list = filteredPhotos().slice();
    if (state.order === 'hue' && state.hue !== null) {
      var hue = state.hue, lens = state.lens;
      list = list.map(function (p, i) {
        var info = colourSortInfo(p, hue, lens);
        return { p: p, i: i, tier: info.tier, d: info.d };
      })
        .sort(function (a, b) { return (a.tier - b.tier) || (a.d - b.d) || (a.i - b.i); })
        .map(function (v) { return v.p; });
    }
    return list;
  }

  // Packing is incremental: only enough rows are ever computed for the
  // tiles currently on the page (state.renderCount), not the whole
  // archive. `packerState` is append-only — placed tiles keep their row/
  // column forever; growing the batch only ever adds more below them.
  // A new packerState is started whenever the ordering/filtering, the
  // column count, or the underlying list length changes.
  var packerState = null;

  function packerKeyFor(list, c) {
    return [state.order, state.hue, state.lens, state.query, state.landing, list.length, c].join('|');
  }

  function toPackItem(p, i, c) {
    var sc = 1, sr = 1;
    var longEdge = Math.max(p.w || 0, p.h || 0);
    if (c >= 3 && i % 10 === 2 && longEdge >= 1600) { sc = 2; sr = 2; }
    return { p: p, sc: sc, sr: sr };
  }

  // Packs only the items between packerState's current progress and
  // `target` (an index into `list`), appending to the persistent grid.
  // A batch can end part-way along a row (or beside a 2x2 tile), so the next
  // batch resumes at the first row that still has an empty cell, not at a
  // fresh row, and its tiles fill those holes first (see `resume` below).
  // Placed tiles never move. Rows before packerState.row are always full.
  function ensurePacked(list, c, target) {
    var key = packerKeyFor(list, c);
    if (!packerState || packerState.key !== key) {
      packerState = { key: key, occ: {}, packed: [], row: 0, doneIndex: 0 };
    }
    if (packerState.doneIndex >= target) return packerState.packed;

    var occ = packerState.occ;
    function free(r, cc, sc, sr) {
      if (cc + sc > c) return false;
      for (var i = 0; i < sr; i++) for (var j = 0; j < sc; j++) if (occ[(r + i) + ':' + (cc + j)] != null) return false;
      return true;
    }
    function rowIsFull(row) {
      for (var j = 0; j < c; j++) if (occ[row + ':' + j] == null) return false;
      return true;
    }

    var queue = [];
    for (var idx = packerState.doneIndex; idx < target; idx++) queue.push(toPackItem(list[idx], idx, c));
    packerState.doneIndex = target;

    var r = packerState.row, guard = 0;
    while (queue.length && guard < 4000) {
      for (var cc = 0; cc < c; cc++) {
        if (occ[r + ':' + cc] != null) continue;
        var pick = -1;
        for (var k = 0; k < queue.length; k++) {
          if (free(r, cc, queue[k].sc, queue[k].sr)) { pick = k; break; }
        }
        if (pick < 0) continue;
        var q = queue.splice(pick, 1)[0];
        for (var i2 = 0; i2 < q.sr; i2++) for (var j2 = 0; j2 < q.sc; j2++) occ[(r + i2) + ':' + (cc + j2)] = 1;
        q.r = r; q.c = cc;
        packerState.packed.push(q);
      }
      r++; guard++;
    }
    // r is one past the last row this batch worked on. Resume at the first
    // row from the old pointer on that still has an empty cell.
    var resume = packerState.row;
    while (resume < r && rowIsFull(resume)) resume++;
    packerState.row = resume;
    return packerState.packed;
  }

  // ---------------------------------------------------------------
  // Tile render + FLIP resort
  // ---------------------------------------------------------------
  var grid = root.querySelector('.bpd-grid');

  function creditParts(p) {
    var parts = [];
    if (p.b) parts.push(p.b);
    if (p.vn) parts.push(p.vn);
    return parts.join(' · ');
  }

  function chipLine2(p) {
    if (p.pr && p.pr.length) return p.pr[0].s || p.pr[0].t;
    if (p.ph) return 'Photography: ' + p.ph;
    return '';
  }

  // Loading placeholder. With a colour picked, tiles still loading show
  // soft variations on that shade (lightness/saturation varied per photo,
  // stable by id) so the wall reads as one wash; otherwise the photo's own
  // dominant colour.
  function idVariation(id) {
    var h = 0, str = String(id);
    for (var i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
    return (Math.abs(h) % 1000) / 1000;
  }
  function placeholderFor(p) {
    if (state.order === 'hue' && state.hue !== null && state.hue !== undefined) {
      var v = idVariation(p.id);
      if (isNeutralHue(state.hue)) {
        return 'hsl(30, ' + Math.round(6 + v * 10) + '%, ' + Math.round(60 + v * 26) + '%)';
      }
      return 'hsl(' + Math.round(state.hue) + ', ' + Math.round(18 + v * 16) + '%, ' +
        Math.round(58 + ((v * 7) % 1) * 26) + '%)';
    }
    return p.c || '#8a8078';
  }

  // Crop focus point (fp, marketing_131): [x, y] per cent across / down.
  // Returns an object-position value, or '' when fp is missing or malformed
  // (then the CSS crop is left exactly as it was).
  function fpPos(p) {
    var f = p && p.fp;
    if (!f || !Array.isArray(f) || f.length !== 2) return '';
    var x = f[0], y = f[1];
    if (typeof x !== 'number' || typeof y !== 'number' || !isFinite(x) || !isFinite(y)) return '';
    if (x < 0 || x > 100 || y < 0 || y > 100) return '';
    return Math.round(x) + '% ' + Math.round(y) + '%';
  }

  function makeTile(p) {
    // A <div role="button">, not a real <button> — the swatch row inside
    // it is itself interactive (tapping a swatch sorts the archive), and
    // a <button> cannot legally contain another <button>.
    var el = document.createElement('div');
    el.className = 'bpd-tile';
    el.setAttribute('role', 'button');
    el.tabIndex = 0;
    el.style.background = placeholderFor(p);
    el.setAttribute('data-id', p.id);
    el.setAttribute('aria-label', 'Open photograph' + (p.b ? ' from ' + p.b + '’s wedding' : ''));
    if (EDIT_ENABLED && isInEdit(p.id)) el.classList.add('bpd-tile--edit');

    var dot = document.createElement('span');
    dot.className = 'bpd-tile__dot';
    dot.setAttribute('aria-hidden', 'true');
    el.appendChild(dot);

    var img = document.createElement('img');
    img.className = 'bpd-tile__img';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.alt = p.a || '';
    var tilePos = fpPos(p);
    if (tilePos) img.style.objectPosition = tilePos;
    img.setAttribute('data-src-pending', '1');
    el.appendChild(img);

    var line1 = creditParts(p);
    var line2 = chipLine2(p);
    if (line1 || line2) {
      var chip = document.createElement('div');
      chip.className = 'bpd-tile__chip';
      if (line1) {
        var l1 = document.createElement('p');
        l1.className = 'bpd-tile__chip-l1';
        l1.textContent = line1;
        chip.appendChild(l1);
      }
      if (line2) {
        var l2 = document.createElement('p');
        l2.className = 'bpd-tile__chip-l2';
        l2.textContent = line2;
        chip.appendChild(l2);
      }
      el.appendChild(chip);
    }

    // Styling colours (`sc`) when present — what's actually in the photo
    // (flowers/attire/decor/cake), not a whole-photo pixel swatch. Falls
    // back to the pixel swatches (`sw`) if the photo has no `sc` yet.
    var swatchHexes = (p.sc && p.sc.length) ? p.sc : (p.sw || []).map(function (sw) {
      return Array.isArray(sw) ? sw[0] : sw.hex;
    });
    if (swatchHexes.length) {
      var row = document.createElement('div');
      row.className = 'bpd-tile__swatches';
      swatchHexes.slice(0, 4).forEach(function (hex) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'bpd-tile__swatch';
        b.style.background = hex;
        b.setAttribute('aria-label', 'Sort the archive to this colour');
        b.addEventListener('click', function (e) {
          e.stopPropagation();
          sortToHue(hex);
        });
        row.appendChild(b);
      });
      el.appendChild(row);
    }

    el.addEventListener('click', function () {
      if (el._suppressClick) { el._suppressClick = false; return; }
      openLightbox(p.id);
    });
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openLightbox(p.id); }
    });
    // long-press (chip-on, and — when EDIT_ENABLED — add/remove from your
    // edit) is wired once, delegated on the grid itself. See
    // initLongPressDelegation() below; nothing touch-related is bound here.

    return el;
  }

  function loadTileImage(el, p, kind) {
    var img = el.querySelector('.bpd-tile__img');
    if (!img.hasAttribute('data-src-pending')) return;
    img.removeAttribute('data-src-pending');
    var attrs = buildImgAttrs(p, kind);
    img.addEventListener('load', function () { img.classList.add('bpd-tile__img--in'); }, { once: true });
    if (attrs.srcset) img.srcset = attrs.srcset;
    img.src = attrs.src;
  }

  // Long-press: one delegated, passive listener on the grid rather than
  // touchstart/touchmove/touchend on every tile — with ~1,900 tiles that
  // was ~5,700 listeners for no benefit, since only one tile can ever be
  // mid-press at a time. 380ms shows the chip (mirrors :hover); 550ms
  // toggles add/remove from your edit (only when EDIT_ENABLED) and
  // suppresses the tap-to-open.
  (function initLongPressDelegation() {
    var chipTimer, editTimer;
    function clear() {
      clearTimeout(chipTimer); clearTimeout(editTimer);
    }
    grid.addEventListener('touchstart', function (e) {
      var el = e.target.closest ? e.target.closest('.bpd-tile') : null;
      if (!el) return;
      chipTimer = setTimeout(function () { el.classList.add('bpd-tile--chip-on'); }, 380);
      if (EDIT_ENABLED) {
        editTimer = setTimeout(function () {
          el._suppressClick = true;
          toggleEdit(el.getAttribute('data-id'));
          if ('vibrate' in navigator) navigator.vibrate(12);
        }, 550);
      }
    }, { passive: true });
    grid.addEventListener('touchend', clear, { passive: true });
    grid.addEventListener('touchmove', clear, { passive: true });
  })();

  var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target;
      var p = byId[el.getAttribute('data-id')];
      if (!p) return;
      var sc = el.getAttribute('data-sc');
      loadTileImage(el, p, sc === '2' ? 'tile2' : 'tile1');
      io.unobserve(el);
    });
  }, { rootMargin: '600px 0px' }) : null;

  // Tracks the order/filter state renderGrid last ran with — a change here
  // (colour, lens, search, landing) means a new view, so the render count
  // drops back to the first batch. Resize and the first→full data swap are
  // NOT in this key: they keep whatever the visitor had already scrolled to.
  var lastFilterKey = null;

  // Landing mode: the generated .bwl-grid (24 server-rendered SEO tiles,
  // above this wall in the DOM — see templates/page.wedding-landing.liquid)
  // stays in the HTML for crawlers and first paint. Hide it (not
  // display:none before JS — a no-JS or slow-JS visitor still gets a tidy
  // grid) the moment this wall has rendered its own first batch. Runs at
  // most once.
  var seoGridHidden = false;
  function hideStaticSeoGridOnce() {
    if (!LANDING_MODE || seoGridHidden) return;
    var seoGrid = document.querySelector('.bwl-grid');
    if (!seoGrid) return;
    seoGrid.classList.add('bwl-grid--hidden');
    seoGridHidden = true;
  }

  function renderGrid() {
    var list = orderedPhotos();
    var c = cols();

    var filterKey = [state.order, state.hue, state.lens, state.query, state.landing].join('|');
    if (filterKey !== lastFilterKey) {
      lastFilterKey = filterKey;
      state.renderCount = INITIAL_BATCH;
    }

    var target = Math.min(state.renderCount, list.length);
    var packed = ensurePacked(list, c, target);

    // FLIP: capture old rects for elements already on the page (bounded to
    // the tiles currently rendered, never the whole archive)
    var oldRects = {};
    if (!REDUCE_MOTION) {
      Object.keys(tileEls).forEach(function (id) {
        var el = tileEls[id];
        if (el.isConnected) oldRects[id] = el.getBoundingClientRect();
      });
    }

    var frag = document.createDocumentFragment();
    var seen = {};
    var mid = packed.length / 2;

    packed.forEach(function (q, i) {
      var p = q.p;
      seen[p.id] = true;
      byId[p.id] = p;
      var el = tileEls[p.id];
      var isNew = !el;
      if (el) el.style.background = placeholderFor(p);
      if (isNew) {
        el = makeTile(p);
        tileEls[p.id] = el;
        // grid-column/row is explicit placement, so a brand-new tile only
        // needs to be attached once — reused tiles never need to move in
        // the DOM even when they're re-sorted, so we don't touch them here.
        frag.appendChild(el);
      } else {
        // the full archive fetch can bring fields the first batch didn't
        // have (e.g. `a` alt text) — sync them onto the existing tile.
        var existingImg = el.querySelector('.bpd-tile__img');
        if (existingImg && existingImg.alt !== (p.a || '')) existingImg.alt = p.a || '';
      }
      el.style.gridColumn = (q.c + 1) + ' / span ' + q.sc;
      el.style.gridRow = (q.r + 1) + ' / span ' + q.sr;
      el.setAttribute('data-sc', q.sc);

      if (io) io.observe(el);
      else loadTileImage(el, p, q.sc === 2 ? 'tile2' : 'tile1');

      if (isNew && !REDUCE_MOTION) {
        el.classList.add('bpd-tile--entering');
        requestAnimationFrame(function () { el.classList.remove('bpd-tile--entering'); });
      }

      if (!REDUCE_MOTION && oldRects[p.id]) {
        var old = oldRects[p.id];
        var delay = Math.min(120, Math.round(Math.abs(i - mid) * 8));
        // apply transform from old position immediately, then release
        el._flipOld = old;
        el._flipDelay = delay;
      }
    });

    // remove tiles that aren't in the current view — either the data set
    // shrank, or a search/lens/colour filter now excludes them, or a
    // resort reset the batch back to the first page.
    // `byId` is left alone: the photo is still valid data, just not shown.
    Object.keys(tileEls).forEach(function (id) {
      if (!seen[id]) {
        var el = tileEls[id];
        if (io) io.unobserve(el);
        el.remove();
        delete tileEls[id];
      }
    });

    grid.appendChild(frag);

    // FLIP second pass: invert then play
    if (!REDUCE_MOTION) {
      requestAnimationFrame(function () {
        Object.keys(tileEls).forEach(function (id) {
          var el = tileEls[id];
          if (!el._flipOld) return;
          var newRect = el.getBoundingClientRect();
          var dx = el._flipOld.left - newRect.left;
          var dy = el._flipOld.top - newRect.top;
          if (!dx && !dy) { delete el._flipOld; return; }
          el.style.transition = 'none';
          el.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
          var delay = el._flipDelay || 0;
          requestAnimationFrame(function () {
            el.style.transition = 'transform 480ms cubic-bezier(.2,.7,.2,1) ' + delay + 'ms';
            el.style.transform = 'none';
          });
          delete el._flipOld;
        });
      });
    }

    renderSparseNote(list.length);
    renderEmptyState(list.length);
    renderCountLine(list.length);
    renderStickyBar(list.length);
    updateSentinel(target < list.length);
    if (packed.length) hideStaticSeoGridOnce();
  }

  // ---------------------------------------------------------------
  // Batch sentinel — a plain sibling below the grid (not part of the CSS
  // grid, so it never takes a packed cell). As it nears the viewport we
  // grow state.renderCount and re-render; ensurePacked() only ever packs
  // the newly-revealed slice, so this stays cheap batch after batch.
  // ---------------------------------------------------------------
  var sentinel = root.querySelector('.bpd-sentinel');
  var sentinelIo = (sentinel && 'IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
    if (!entries[entries.length - 1].isIntersecting) return;
    if (state.renderCount >= orderedPhotos().length) return;
    state.renderCount += BATCH_SIZE;
    renderGrid();
  }, { rootMargin: '1200px 0px' }) : null;

  // Site-wide landings nav (britten-landings-nav.liquid) — visually
  // collapsed (bpd-nav-wrap--collapsed, see britten-landings-nav.css)
  // until every batch for the current view has loaded, so it never
  // appears "in view" mid-scroll and interferes with the sentinel above
  // it. Always present in the DOM for crawlers regardless.
  var navWrap = root.querySelector('[data-bpd-nav-wrap]');
  if (navWrap) {
    navWrap.addEventListener('click', function (e) {
      var link = e.target && e.target.closest ? e.target.closest('.bwn a[data-nav-to]') : null;
      if (!link) return;
      track('landing_nav_click', { from: 'wall', to: link.getAttribute('data-nav-to') || '' });
    });
  }

  function updateSentinel(hasMore) {
    if (navWrap) navWrap.classList.toggle('bpd-nav-wrap--collapsed', hasMore);
    if (!sentinel) return;
    sentinel.hidden = !hasMore;
    if (sentinelIo) sentinelIo.observe(sentinel);
    else if (hasMore) {
      // no IntersectionObserver support: fall back to loading everything
      state.renderCount = Infinity;
      renderGrid();
    }
  }

  // Lens-sparse note — README: "show the real count plainly ... never pad
  // with repeats." Only makes sense while a lens is active with a thin
  // result; a plain search/colour view of 40+ doesn't need this.
  function renderSparseNote(n) {
    var note = root.querySelector('.bpd-sparse');
    if (!note) return;
    if (state.lens && n > 0 && n < 40) {
      note.hidden = false;
      note.textContent = n + ' photograph' + (n === 1 ? '' : 's') + ' so far. More arrive as brides share their day — ' +
        'add a colour to narrow them further, or turn the lens off to see every frame.';
    } else {
      note.hidden = true;
    }
  }

  function renderEmptyState(n) {
    var empty = root.querySelector('.bpd-empty');
    if (!empty) return;
    empty.hidden = n > 0;
  }

  function renderCountLine(n) {
    var el = root.querySelector('[data-bpd-count]');
    if (!el) return;
    var bits = [];
    bits.push(fmt(n) + ' photograph' + (n === 1 ? '' : 's'));
    if (state.query) bits.push('for “' + state.query + '”');
    if (state.lens) bits.push('in ' + (LENS_LABELS[state.lens] || state.lens));
    var landing = landingByHandle(state.landing);
    // Locked landing mode: the page itself already says which landing this
    // is, so "in this palette" (or "in this setting"/"in this season") reads
    // better than repeating the landing's own name back.
    if (landing && LANDING_MODE) bits.push('in this ' + (LANDING_KIND_NOUN[landing.k] || 'selection'));
    else if (landing) bits.push('in ' + landing.t);
    if (state.order === 'hue' && state.hue !== null) {
      bits.push(LANDING_MODE ? 'sorted within this shade' : 'sorted by colour from here');
    }
    el.textContent = bits.join(' ') + '.';
  }

  // ---------------------------------------------------------------
  // Sticky control bar — count + "Back to top" / active-filter chip.
  // The spectrum and lenses in the sticky bar are kept in sync from
  // renderSpectrum()/renderLenses() above; this just handles the two
  // bits unique to the compact bar. See initSticky (near boot) for the
  // show/hide-on-scroll behaviour itself.
  // ---------------------------------------------------------------
  var stickyCountEl = root.querySelector('[data-bpd-sticky-count]');
  var stickyChip = root.querySelector('[data-bpd-sticky-chip]');

  function renderStickyBar(n) {
    if (stickyCountEl) stickyCountEl.textContent = fmt(n) + ' photograph' + (n === 1 ? '' : 's') + '.';
    if (!stickyChip) return;
    var filtered = !!(state.query || state.lens || state.landing || (state.order === 'hue' && state.hue !== null));
    stickyChip.hidden = false;
    stickyChip.textContent = filtered ? 'Filtered · Back to top' : 'Back to top';
  }

  if (stickyChip) stickyChip.addEventListener('click', scrollWallToTop);

  // Scrolls the wall back to the top of its own controls — used whenever
  // a filter changes from the sticky bar, so the visitor lands where the
  // newly-filtered results start rather than mid-scroll through the old set.
  function scrollWallToTop() {
    var target = root.querySelector('.bpd-controls');
    if (!target) return;
    var top = target.getBoundingClientRect().top + window.pageYOffset - stickyHeaderOffset - 8;
    window.scrollTo({ top: Math.max(0, top), behavior: REDUCE_MOTION ? 'auto' : 'smooth' });
  }

  // ---------------------------------------------------------------
  // Landing filter (?landing=<handle>) — a removable chip; a colour
  // landing also sets the spectrum to its hue. Unknown handles are
  // ignored (no chip, no filter).
  // ---------------------------------------------------------------
  var landingChip = root.querySelector('[data-bpd-landing-chip]');

  function renderLandingChip() {
    if (!landingChip) return;
    var landing = landingByHandle(state.landing);
    if (!landing) { landingChip.hidden = true; return; }
    landingChip.hidden = false;
    landingChip.innerHTML = '';
    var label = document.createElement('span');
    label.textContent = landing.t;
    landingChip.appendChild(label);
    var x = document.createElement('span');
    x.className = 'bpd-landing-chip__x';
    x.setAttribute('aria-hidden', 'true');
    x.textContent = '✕';
    landingChip.appendChild(x);
    landingChip.setAttribute('aria-label', 'Remove filter: ' + landing.t);
  }

  var landingTracked = null; // last handle tracked, so a re-resolve (e.g. once
                             // the full archive loads) doesn't double-fire
  function setLanding(handle, opts) {
    // Locked landing mode: the visitor can't clear it (no chip is even
    // rendered — see the snippet), so a stray `setLanding(null)` call
    // (e.g. from stale ?landing= URL handling) can never unlock it.
    if (LANDING_MODE && LOCKED_LANDING) handle = LOCKED_LANDING;
    var landing = landingByHandle(handle);
    state.landing = landing ? handle : null;
    state.shadeStops = computeShadeStops(landing);
    if (landing && landing.k === 'colour') {
      if (state.shadeStops) {
        // Default to the stop closest to mid-lightness (the "main" tone,
        // not the palest or darkest end of the ramp/palette).
        var bestIdx = 0, bestD = Infinity;
        state.shadeStops.forEach(function (s, i) {
          var d = Math.abs(s.l - 0.6);
          if (d < bestD) { bestD = d; bestIdx = i; }
        });
        state.order = 'hue';
        state.hue = 'shade:' + bestIdx;
        renderSpectrum();
        updateColourUrl();
      } else {
        var lockHue = (typeof landing.hue !== 'undefined' && landing.hue !== null)
          ? landing.hue
          : LANDING_HUE_FALLBACK[landing.h];
        if (typeof lockHue !== 'undefined') {
          state.order = 'hue';
          state.hue = lockHue;
          renderSpectrum();
          updateColourUrl();
        }
      }
    }
    renderLandingChip();
    updateLandingLabels(landing);
    updateLandingUrl();
    updateViewSwitch();
    renderGrid();
    if (landing && landingTracked !== handle && !(opts && opts.quiet)) {
      landingTracked = handle;
      track('wall_filter', { type: 'landing', value: handle });
    }
    if (!landing) landingTracked = null;
  }

  if (landingChip) {
    landingChip.addEventListener('click', function () { setLanding(null); });
  }

  // Spectrum copy in landing mode: COLOUR landings only get "Sorted within
  // {name}, from this shade" (landing name, lower case, "weddings" dropped
  // — the page is already titled "Blush pink weddings"; repeating the
  // whole title here reads clumsily next to "from this shade") alongside
  // their own shade strip. Setting/season/country landings show the
  // NORMAL full hue spectrum, same label as the main wall ("Sorted from
  // this hue", the markup's own default text) — never the shade wording,
  // since there is no shade strip to describe. No-op outside landing mode,
  // for a non-colour landing, or before the landing has resolved.
  function updateLandingLabels(landing) {
    if (!LANDING_MODE || !landing || landing.k !== 'colour') return;
    var label = 'Sorted within ' + landing.t.replace(/\s+weddings$/i, '').toLowerCase() + ', from this shade';
    root.querySelectorAll('.bpd-spectrum__labels span:first-child').forEach(function (el) {
      el.textContent = label;
    });
    // The archive's right-hand "Ivory · Taupe · Stone · Dark" key describes
    // the neutral end of the FULL hue spectrum — meaningless once the
    // track only shows this landing's own shade stops (each already
    // labelled underneath). Hidden via CSS (.bpd-spectrum__track--shade,
    // set in renderSpectrum) rather than here so it re-appears correctly
    // if the landing turns out not to have shade stops after all.
  }

  // ---------------------------------------------------------------
  // Colour sort + spectrum — open by default, no toggle (Turn 3)
  // ---------------------------------------------------------------
  // The sticky bar's track comes first in the DOM, so exclude it explicitly
  // or the main bar never gets its stops (seen live: empty spectrum).
  var spectrumTrack = root.querySelector('.bpd-spectrum__track:not([data-bpd-sticky-track])');
  var spectrumThumb = spectrumTrack ? spectrumTrack.querySelector('.bpd-spectrum__thumb') : null;
  // The sticky bar (see initSticky) carries its own copy of the spectrum
  // track/thumb — same state, same stops, kept in sync alongside the
  // main one everywhere renderSpectrum/positionThumb run.
  var stickyTrack = root.querySelector('[data-bpd-sticky-track]');
  var stickyThumb = stickyTrack ? stickyTrack.querySelector('.bpd-spectrum__thumb') : null;

  function spectrumContexts() {
    var list = [];
    if (spectrumTrack) list.push({ el: spectrumTrack, thumb: spectrumThumb, isSticky: false });
    if (stickyTrack) list.push({ el: stickyTrack, thumb: stickyThumb, isSticky: true });
    return list;
  }

  // Display only: the AI's hexes are vivid, so the bar shows them muted
  // (less saturation, a touch of the canvas mixed in) to sit with the
  // page's palette. Sorting still uses the true colours.
  function softenHex(hex) {
    var n = parseInt(String(hex).replace('#', ''), 16);
    if (isNaN(n)) return hex;
    var r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    var grey = 0.3 * r + 0.59 * g + 0.11 * b;
    var SAT = 0.6, MIX = 0.18, cr = 243, cg = 238, cb = 233;
    function ch(v, c) {
      v = grey + (v - grey) * SAT;
      return Math.round(v * (1 - MIX) + c * MIX);
    }
    return 'rgb(' + ch(r, cr) + ',' + ch(g, cg) + ',' + ch(b, cb) + ')';
  }

  function renderSpectrum() {
    var stops = spectrumStops();
    var isShade = !!(LANDING_MODE && state.shadeStops);
    spectrumContexts().forEach(function (ctx) {
      ctx.el.classList.toggle('bpd-spectrum__track--shade', isShade);
      ctx.el.querySelectorAll('.bpd-spectrum__stop').forEach(function (n) { n.remove(); });
      stops.forEach(function (s) {
        var stop = document.createElement('span');
        stop.className = 'bpd-spectrum__stop';
        stop.style.background = softenHex(s.c);
        // Named stops (the shade strip, from a real landing.pal or the
        // generated ramp) get a Figtree label underneath, desktop only
        // (see .bpd-spectrum__stop-name in the CSS) — same treatment as
        // the header's own palette swatches. Not rendered in the sticky
        // bar's compact track, which just mirrors the colours + thumb.
        if (isShade && s.name && !ctx.isSticky) {
          var label = document.createElement('span');
          label.className = 'bpd-spectrum__stop-name';
          label.textContent = s.name;
          stop.appendChild(label);
        }
        ctx.el.insertBefore(stop, ctx.thumb);
      });
    });
    positionThumb(stops);
  }

  // Plain-English name for any spectrum stop, for the slider's
  // aria-valuetext — a shade stop already carries one (`.name`), a neutral
  // stop is Title-cased from its id (ivory/linen/taupe/stone/espresso/
  // dark), and a plain hue bucket maps onto a coarse, familiar colour name.
  var HUE_NAME_BANDS = [
    [15, 'Red'], [45, 'Orange'], [70, 'Gold'], [100, 'Yellow-green'],
    [150, 'Green'], [190, 'Teal'], [230, 'Blue'], [260, 'Indigo'],
    [290, 'Purple'], [330, 'Pink'], [361, 'Red']
  ];
  function hueName(h) {
    h = ((h % 360) + 360) % 360;
    for (var i = 0; i < HUE_NAME_BANDS.length; i++) if (h < HUE_NAME_BANDS[i][0]) return HUE_NAME_BANDS[i][1];
    return 'Red';
  }
  function stopName(stop) {
    if (!stop) return 'Unsorted';
    if (stop.name) return stop.name;
    if (isNeutralHue(stop.hue)) {
      if (stop.hue === 'neutral') return 'Neutral';
      var id = stop.hue.slice('neutral-'.length);
      return id.charAt(0).toUpperCase() + id.slice(1);
    }
    if (typeof stop.hue === 'number') return hueName(stop.hue);
    return 'Unsorted';
  }

  function positionThumb(stops) {
    var idx = -1;
    if (state.order === 'hue' && state.hue !== null) {
      idx = stops.findIndex(function (s) { return s.hue === state.hue; });
      if (idx < 0) idx = 0;
    }
    var pct = (idx >= 0 && stops.length > 1) ? idx / (stops.length - 1) : 0;
    var current = idx >= 0 ? stops[idx] : null;
    spectrumContexts().forEach(function (ctx) {
      if (!ctx.thumb) return;
      ctx.thumb.style.display = idx < 0 ? 'none' : '';
      ctx.thumb.style.left = (pct * 100) + '%';
      ctx.el.setAttribute('aria-valuemin', '0');
      ctx.el.setAttribute('aria-valuemax', String(Math.max(0, stops.length - 1)));
      ctx.el.setAttribute('aria-valuenow', String(idx < 0 ? 0 : idx));
      ctx.el.setAttribute('aria-valuetext', stopName(current));
    });
  }

  // Shared by sortToHue (tile swatch taps) and sortToShade (landing header
  // palette taps) — applies the hue, re-renders, but leaves tracking to
  // the caller so each can log its own event `type`.
  function applyHueSort(hex) {
    var h = hsl(hex);
    state.order = 'hue';
    if (state.shadeStops) {
      // A colour landing's track only has its own shade stops (see
      // computeShadeStops) — a tap (tile hover swatch, or the header
      // palette once stage 2 ships) snaps to the closest one rather than
      // an arbitrary hue outside that strip.
      var bestIdx = 0, bestD = Infinity;
      state.shadeStops.forEach(function (s, i) {
        var d = shadeDist(h, s);
        if (d < bestD) { bestD = d; bestIdx = i; }
      });
      state.hue = 'shade:' + bestIdx;
    } else {
      state.hue = h.s < 0.1 ? nearestNeutralHue(h.l) : Math.round(h.h);
    }
    renderSpectrum();
    renderGrid();
    updateColourUrl();
  }

  function sortToHue(hex) {
    applyHueSort(hex);
    track('wall_filter', { type: 'colour', value: state.hue });
  }

  // ---------------------------------------------------------------
  // Public API for the landing page header (stage 2: palette swatches in
  // the generated .bwl-head) to sort this wall to a named shade without a
  // page reload or leaving the page. Exposed on the root element so the
  // landing page's own script can call it once this one has booted:
  //   document.querySelector('.bpd').bpdSortToHex('#e9c7c4')
  // Distinct from sortToHue (tile swatch taps, tracked as type:'colour')
  // so analytics can tell a header palette tap from an in-tile one; the
  // hex itself is the tracked value (not the rounded hue) per spec.
  // ---------------------------------------------------------------
  function sortToShade(hex) {
    applyHueSort(hex);
    scrollWallToTop();
    track('wall_filter', { type: 'shade', value: hex });
  }
  root.bpdSortToHex = sortToShade;

  function pickFromPointer(clientX, trackEl) {
    var stops = spectrumStops();
    if (!stops.length) return;
    var rect = trackEl.getBoundingClientRect();
    var f = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    var idx = Math.min(stops.length - 1, Math.floor(f * stops.length));
    state.order = 'hue';
    state.hue = stops[idx].hue;
    positionThumb(stops);
    updateColourUrl();
  }

  // Left/Right arrow keys — the slider's own keyboard equivalent of a drag,
  // stepping to the next/previous stop rather than an arbitrary point.
  function stepSpectrum(dir) {
    var stops = spectrumStops();
    if (!stops.length) return;
    var idx = -1;
    if (state.order === 'hue' && state.hue !== null) {
      idx = stops.findIndex(function (s) { return s.hue === state.hue; });
    }
    var next = ((idx < 0 ? 0 : idx) + dir + stops.length) % stops.length;
    state.order = 'hue';
    state.hue = stops[next].hue;
    positionThumb(stops);
    renderGrid();
    updateColourUrl();
    track('wall_filter', { type: 'colour', value: state.hue });
  }

  var dragging = false;
  var dragRaf = null;

  function wireSpectrumTrack(trackEl, isSticky) {
    if (!trackEl) return;
    trackEl.addEventListener('pointerdown', function (e) {
      dragging = true;
      trackEl.setPointerCapture(e.pointerId);
      pickFromPointer(e.clientX, trackEl);
      scheduleResort();
    });
    trackEl.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      pickFromPointer(e.clientX, trackEl);
      scheduleResort();
    });
    trackEl.addEventListener('pointerup', function () {
      dragging = false;
      if (state.order === 'hue' && state.hue !== null) track('wall_filter', { type: 'colour', value: state.hue });
      if (isSticky) scrollWallToTop();
    });
    trackEl.addEventListener('pointercancel', function () { dragging = false; });
    trackEl.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); stepSpectrum(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); stepSpectrum(-1); }
    });
  }

  wireSpectrumTrack(spectrumTrack, false);
  wireSpectrumTrack(stickyTrack, true);

  function scheduleResort() {
    if (dragRaf) return;
    dragRaf = requestAnimationFrame(function () {
      dragRaf = null;
      renderGrid();
    });
  }

  // ---------------------------------------------------------------
  // Search — text input, combinable with colour and lens
  // ---------------------------------------------------------------
  var searchInput = root.querySelector('[data-bpd-search]');
  var searchRaf = null;

  var searchTrackTimer = null;
  function setQuery(q) {
    state.query = (q || '').trim().toLowerCase();
    updateSearchUrl();
    updateViewSwitch();
    renderGrid();
    // debounced: fire once typing pauses, not on every keystroke
    clearTimeout(searchTrackTimer);
    if (state.query) {
      searchTrackTimer = setTimeout(function () {
        track('wall_filter', { type: 'search', value: state.query });
      }, 500);
    }
  }

  if (searchInput) {
    searchInput.addEventListener('input', function () {
      var val = searchInput.value;
      if (searchRaf) cancelAnimationFrame(searchRaf);
      searchRaf = requestAnimationFrame(function () { setQuery(val); });
    });
  }

  // Sticky bar's own search — same shared setQuery(), kept in sync with
  // the main input in both directions. On mobile it starts collapsed
  // behind an icon button (see .bpd-sticky__search--open in the CSS);
  // committing a search (Enter, or leaving the field) scrolls the wall
  // back to its top, same as any other sticky-bar filter change.
  var stickySearchWrap = root.querySelector('[data-bpd-sticky-search]');
  var stickySearchToggle = root.querySelector('[data-bpd-sticky-search-toggle]');
  var stickySearchInput = root.querySelector('[data-bpd-sticky-search-input]');
  var stickySearchRaf = null;

  if (stickySearchToggle && stickySearchWrap && stickySearchInput) {
    stickySearchToggle.addEventListener('click', function () {
      stickySearchWrap.classList.add('bpd-sticky__search--open');
      stickySearchInput.focus();
    });
    stickySearchInput.addEventListener('blur', function () {
      if (!stickySearchInput.value) stickySearchWrap.classList.remove('bpd-sticky__search--open');
    });
  }
  if (stickySearchInput) {
    stickySearchInput.addEventListener('input', function () {
      var val = stickySearchInput.value;
      if (searchInput && searchInput.value !== val) searchInput.value = val;
      if (stickySearchRaf) cancelAnimationFrame(stickySearchRaf);
      stickySearchRaf = requestAnimationFrame(function () { setQuery(val); });
    });
    stickySearchInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') scrollWallToTop();
    });
    stickySearchInput.addEventListener('blur', function () {
      if (stickySearchInput.value) scrollWallToTop();
    });
  }
  if (searchInput && stickySearchInput) {
    searchInput.addEventListener('input', function () {
      if (stickySearchInput.value !== searchInput.value) stickySearchInput.value = searchInput.value;
    });
  }

  // ---------------------------------------------------------------
  // Lenses (1e) — hidden entirely until a photo carries `l`. Single
  // active lens at a time; tapping the active pill turns it off.
  // ---------------------------------------------------------------
  var lensesRoot = root.querySelector('[data-bpd-lenses]');
  var stickyLensesRoot = root.querySelector('[data-bpd-sticky-lenses]');
  var momentSteps = root.querySelectorAll('[data-bpd-step-moment]');

  function lensCounts() {
    var counts = {};
    state.photos.forEach(function (p) {
      // Scoped to the active landing filter (locked in landing mode, or the
      // removable ?landing= chip on the archive page) — "within-landing
      // counts", not archive-wide totals, whenever one is set.
      if (state.landing && !matchesLanding(p, state.landing)) return;
      (p.l || []).forEach(function (id) { counts[id] = (counts[id] || 0) + 1; });
    });
    return counts;
  }

  // Step 3 ("Keep an edit") is out for now with Your edit — see
  // EDIT_ENABLED — and its markup isn't rendered at all.
  var STEP_LABELS = { 1: 'Sort by colour', 2: 'Pick a moment' };

  function updateHasLenses() {
    state.hasLenses = state.photos.some(function (p) { return p.l && p.l.length; });
    momentSteps.forEach(function (el) { el.hidden = !state.hasLenses; });
    renumberSteps();
  }

  // Renumber the visible how-it-works steps (01, 02...) — when there's no
  // lens data yet, "Pick a moment" is hidden and "Keep an edit" becomes 02.
  function renumberSteps() {
    var visible = state.hasLenses ? [1, 2] : [1];
    var els = root.querySelectorAll('[data-bpd-step-no]');
    var i = 0;
    els.forEach(function (no) {
      var original = parseInt(no.getAttribute('data-bpd-step-no'), 10);
      if (visible.indexOf(original) < 0) return;
      i += 1;
      no.textContent = ('0' + i).slice(-2) + ' · ' + STEP_LABELS[original];
    });
  }

  function renderLenses() {
    var containers = [lensesRoot, stickyLensesRoot].filter(Boolean);
    if (!containers.length) return;
    if (!state.hasLenses) { containers.forEach(function (c) { c.hidden = true; }); return; }
    var counts = lensCounts();
    var present = LENS_ORDER.filter(function (id) { return counts[id] > 0; });
    containers.forEach(function (c) {
      var isSticky = c === stickyLensesRoot;
      c.hidden = present.length < 1;
      c.innerHTML = '';
      present.forEach(function (id) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bpd-lens' + (state.lens === id ? ' bpd-lens--active' : '');
        var label = document.createElement('span');
        label.textContent = LENS_LABELS[id] || id;
        btn.appendChild(label);
        var count = document.createElement('span');
        count.className = 'bpd-lens__count';
        count.textContent = counts[id];
        btn.appendChild(count);
        btn.addEventListener('click', function () {
          toggleLens(id);
          if (isSticky) scrollWallToTop();
        });
        c.appendChild(btn);
      });
    });
  }

  function toggleLens(id) {
    state.lens = state.lens === id ? null : id;
    renderLenses();
    updateLensUrl();
    renderGrid();
    if (state.lens) track('wall_filter', { type: 'lens', value: state.lens });
  }

  // ---------------------------------------------------------------
  // Weddings / Moments switch — shares filters with /pages/real-brides,
  // the unified archive (Photos | Weddings). Its state is query params
  // (never `view`, which Shopify reserves): a landing maps to
  //   colour  -> colour=<hue number>   (Photos view's colour sort)
  //   setting -> setting=<v>           (church, country_house, ...)
  //   season  -> season=<v>
  //   country -> country=<ISO code>    (landing.cc; falls back to q=<name>
  //                                     for data generated before `cc`)
  // and search text carries over as `q`. The link opens the Weddings view
  // (show=weddings). Lens has no equivalent there and is dropped.
  // ---------------------------------------------------------------
  // Landing mode count line ("N photographs in this palette/setting/
  // season/selection") — see renderCountLine.
  var LANDING_KIND_NOUN = { colour: 'palette', setting: 'setting', season: 'season', country: 'selection' };
  // Fallback hue for colour landings whose data doesn't carry `hue` yet
  // (today's assets/britten-picture-desk.json `landings` entries only have
  // h/t/k/v/n — see build_picture_desk_data.py). Without this the spectrum
  // never parks and the loading placeholders (each photo's own dominant
  // colour, unsorted) can read as any hue at all — seen live on
  // blush-pink-weddings as green. Keyed by landing handle; only used for
  // `k: 'colour'` landings. Remove once the pipeline ships a real `hue`
  // per landing (setLanding already prefers `landing.hue` when present).
  var LANDING_HUE_FALLBACK = {
    'blush-pink-weddings': 350,
    'burgundy-weddings': 345,
    'navy-weddings': 225,
    'dusty-blue-weddings': 205,
    'sage-green-weddings': 95,
    'emerald-green-weddings': 150,
    'lilac-weddings': 275,
    'terracotta-weddings': 18,
    'mustard-weddings': 45
  };
  var viewSwitchWeddingsLink = root.querySelector('[data-bpd-view="weddings"]');

  // The archive param(s) for one landing, as [[name, value], ...].
  function archiveParamsForLanding(landing) {
    if (!landing) return [];
    if (landing.k === 'colour') {
      var hue = (typeof landing.hue !== 'undefined' && landing.hue !== null)
        ? landing.hue
        : LANDING_HUE_FALLBACK[landing.h];
      return (typeof hue !== 'undefined' && hue !== null) ? [['colour', hue]] : [];
    }
    if (landing.k === 'setting' || landing.k === 'season') return [[landing.k, landing.v]];
    if (landing.k === 'country') return [landing.cc ? ['country', landing.cc] : ['q', landing.v]];
    return [];
  }

  function realBridesUrl() {
    var landing = landingByHandle(state.landing);
    var params = archiveParamsForLanding(landing);
    // Search text is `q`; a country landing's own name fallback also uses
    // `q`, so the visitor's own search wins.
    if (state.query) params = params.filter(function (kv) { return kv[0] !== 'q'; }).concat([['q', state.query]]);
    var parts = ['show=weddings'];
    params.forEach(function (kv) { parts.push(kv[0] + '=' + encodeURIComponent(kv[1])); });
    return ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root_url) || '').replace(/\/+$/, '') + '/pages/real-brides?' + parts.join('&');
  }

  function updateViewSwitch() {
    if (!viewSwitchWeddingsLink) return;
    viewSwitchWeddingsLink.setAttribute('href', realBridesUrl());
  }

  if (viewSwitchWeddingsLink) {
    viewSwitchWeddingsLink.addEventListener('click', function () {
      track('wall_switch', { to: 'weddings' });
    });
  }

  // ---------------------------------------------------------------
  // "No gaps" — visitor toggle (main bar + sticky bar on desktop),
  // not a theme-editor setting. Adds/removes bpd--touch on the root,
  // which zeroes .bpd-grid's CSS `gap` everywhere (incl. the 2x2
  // feature tiles) — pure CSS, no re-pack needed, since the packer
  // places tiles by grid-column/row span, never pixel maths (see
  // toPackItem/ensurePacked above). Remembered in localStorage so a
  // returning visitor keeps their choice; wrapped in try/catch, same
  // pattern as Your edit's loadEdit/saveEdit.
  // ---------------------------------------------------------------
  var GAPS_STORAGE_KEY = 'britten.pictureDesk.noGaps';
  var gapsInputs = root.querySelectorAll('[data-bpd-gaps-input], [data-bpd-sticky-gaps-input]');

  function loadNoGaps() {
    try {
      // On by default: photos touch unless the visitor has turned it off.
      return window.localStorage.getItem(GAPS_STORAGE_KEY) !== '0';
    } catch (e) {
      log('could not read no-gaps from storage: ' + e);
      return true;
    }
  }

  function saveNoGaps(on) {
    try {
      window.localStorage.setItem(GAPS_STORAGE_KEY, on ? '1' : '0');
    } catch (e) {
      log('could not save no-gaps to storage: ' + e);
    }
  }

  function applyNoGaps(on, opts) {
    root.classList.toggle('bpd--touch', on);
    for (var i = 0; i < gapsInputs.length; i++) gapsInputs[i].checked = on;
    if (!(opts && opts.quiet)) track('wall_filter', { type: 'gaps', value: on ? 'on' : 'off' });
  }

  function initGapsToggle() {
    if (!gapsInputs.length) return;
    applyNoGaps(loadNoGaps(), { quiet: true });
    for (var i = 0; i < gapsInputs.length; i++) {
      gapsInputs[i].addEventListener('change', function (e) {
        var on = e.target.checked;
        saveNoGaps(on);
        applyNoGaps(on);
      });
    }
  }

  // ---------------------------------------------------------------
  // URL state
  // ---------------------------------------------------------------
  function currentUrl() { return new URL(window.location.href); }

  function updateColourUrl() {
    var url = currentUrl();
    if (state.order === 'hue' && state.hue !== null) url.searchParams.set('colour', state.hue);
    else url.searchParams.delete('colour');
    if (url.searchParams.get('p')) return; // don't fight the lightbox param
    history.replaceState(history.state, '', url.toString());
  }

  function readColourFromUrl() {
    var url = currentUrl();
    var c = url.searchParams.get('colour');
    if (c === null) return;
    if (isNeutralHue(c)) { state.order = 'hue'; state.hue = c; }
    else if (!isNaN(parseFloat(c))) { state.order = 'hue'; state.hue = parseFloat(c); }
  }

  function updateLensUrl() {
    var url = currentUrl();
    if (state.lens) url.searchParams.set('lens', state.lens);
    else url.searchParams.delete('lens');
    if (url.searchParams.get('p')) return;
    history.replaceState(history.state, '', url.toString());
  }

  function readLensFromUrl() {
    var url = currentUrl();
    var lens = url.searchParams.get('lens');
    if (lens) state.lens = lens;
  }

  function updateLandingUrl() {
    var url = currentUrl();
    if (state.landing) url.searchParams.set('landing', state.landing);
    else url.searchParams.delete('landing');
    if (url.searchParams.get('p')) return;
    history.replaceState(history.state, '', url.toString());
  }

  // Read + validate ?landing= against the loaded landings list. Called once
  // at boot (first batch, may not have `landings` yet) and again once the
  // full archive (with `landings`) has loaded.
  function readLandingFromUrl() {
    var url = currentUrl();
    var handle = url.searchParams.get('landing');
    if (!handle) return;
    if (landingByHandle(handle)) {
      setLanding(handle);
    } else if (!state.landings.length) {
      // landings not loaded yet — keep the requested handle pending
      state._pendingLanding = handle;
    }
  }

  function updateSearchUrl() {
    var url = currentUrl();
    if (state.query) url.searchParams.set('q', state.query);
    else url.searchParams.delete('q');
    if (url.searchParams.get('p')) return;
    history.replaceState(history.state, '', url.toString());
  }

  function readSearchFromUrl() {
    var url = currentUrl();
    var q = url.searchParams.get('q');
    if (q) {
      state.query = q.trim().toLowerCase();
      if (searchInput) searchInput.value = q;
    }
  }

  // ---------------------------------------------------------------
  // Lightbox
  // ---------------------------------------------------------------
  var lb = root.querySelector('.bpd-lb');
  var lbImg = lb ? lb.querySelector('.bpd-lb__img') : null;
  var lbClose = lb ? lb.querySelector('.bpd-lb__close') : null;
  var lbPrev = lb ? lb.querySelector('.bpd-lb__nav--prev') : null;
  var lbNext = lb ? lb.querySelector('.bpd-lb__nav--next') : null;
  var lbPanel = lb ? lb.querySelector('.bpd-lb__panel') : null;

  function wedSentence(p) {
    if (p.vn && p.rg) return 'Married at ' + p.vn + ' in ' + p.rg + '.';
    if (p.vn) return 'Married at ' + p.vn + '.';
    if (p.rg) return 'A wedding in ' + p.rg + '.';
    if (p.ct) return 'A wedding in ' + p.ct + '.';
    return '';
  }

  function renderLightboxPanel(p) {
    lbPanel.innerHTML = '';

    var row = document.createElement('div');
    row.className = 'bpd-lb__row';
    var eyebrow = document.createElement('p');
    eyebrow.className = 'bpd-lb__eyebrow';
    eyebrow.textContent = 'From the archive';
    row.appendChild(eyebrow);
    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'bpd-lb__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '✕';
    closeBtn.addEventListener('click', closeLightbox);
    row.appendChild(closeBtn);
    lbPanel.appendChild(row);

    if (p.b) {
      var name = document.createElement('h2');
      name.className = 'bpd-lb__name';
      name.textContent = p.b;
      lbPanel.appendChild(name);
    }

    var sentence = wedSentence(p);
    if (sentence) {
      var sEl = document.createElement('p');
      sEl.className = 'bpd-lb__sentence';
      sEl.textContent = sentence;
      lbPanel.appendChild(sEl);
    }

    if (p.pr && p.pr.length) {
      var hr = document.createElement('hr');
      hr.className = 'bpd-lb__hr';
      lbPanel.appendChild(hr);

      var shown = p.pr.slice(0, 2);
      shown.forEach(function (prod) {
        var block = document.createElement('div');
        block.className = 'bpd-lb__product';
        var pe = document.createElement('p');
        pe.className = 'bpd-lb__product-eyebrow';
        // product_type / a veil-vs-other flag isn't available in the rb_*
        // views or this build's data (rb_wedding_products only carries
        // wedding_id/product_handle/product_title/confidence) — so a
        // Sarah-confirmed item never claims to be "the veil" when it might
        // be a hair vine or another piece (audit: Avani, a hair vine,
        // labelled "THE VEIL SHE WORE"). Revisit if a type ever lands here.
        pe.textContent = p.pw ? 'The pieces she wore' : 'From her wedding';
        block.appendChild(pe);
        var pt = document.createElement('p');
        pt.className = 'bpd-lb__product-title';
        pt.textContent = prod.t;
        block.appendChild(pt);
        var link = document.createElement('a');
        link.className = 'bpd-lb__product-link';
        link.href = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root_url) || '').replace(/\/+$/, '') + '/products/' + prod.h;
        link.textContent = 'Shop ' + prod.t;
        link.addEventListener('click', function () {
          track('wall_shop_click', {
            photo_id: p.id,
            product_handle: prod.h,
            context: 'lightbox',
            landing: state.landing || null
          });
        });
        block.appendChild(link);
        lbPanel.appendChild(block);
      });
      if (p.pr.length > 2) {
        var more = document.createElement('p');
        more.className = 'bpd-lb__product-more';
        more.textContent = 'and ' + (p.pr.length - 2) + ' more';
        lbPanel.appendChild(more);
      }
    }

    // "More like this" — up to 3 of the photo's landings, under the veil
    // block. Tolerates missing `lp` / `landings` (nothing renders).
    if (p.lp && p.lp.length && state.landings.length) {
      var likeThis = p.lp.map(landingByHandle).filter(Boolean).slice(0, 3);
      if (likeThis.length) {
        var likeBlock = document.createElement('div');
        likeBlock.className = 'bpd-lb__landings';
        var likeTitle = document.createElement('p');
        likeTitle.className = 'bpd-lb__landings-title';
        likeTitle.textContent = 'More like this';
        likeBlock.appendChild(likeTitle);
        var likeList = document.createElement('div');
        likeList.className = 'bpd-lb__landings-list';
        likeThis.forEach(function (l) {
          var a = document.createElement('a');
          a.className = 'bpd-lb__landing-link';
          a.href = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root_url) || '').replace(/\/+$/, '') + '/pages/' + l.h;
          a.textContent = l.t;
          likeList.appendChild(a);
        });
        likeBlock.appendChild(likeList);
        lbPanel.appendChild(likeBlock);
      }
    }

    var bottom = document.createElement('div');
    bottom.className = 'bpd-lb__bottom';

    if (p.sw && p.sw.length) {
      var pal = document.createElement('div');
      pal.className = 'bpd-lb__palette';
      var sw = document.createElement('div');
      sw.className = 'bpd-lb__swatches';
      p.sw.slice(0, 4).forEach(function (s) {
        var hex = Array.isArray(s) ? s[0] : s.hex;
        var span = document.createElement('span');
        span.style.background = hex;
        sw.appendChild(span);
      });
      pal.appendChild(sw);
      bottom.appendChild(pal);
    }

    var links = document.createElement('div');
    links.className = 'bpd-lb__links';
    if (EDIT_ENABLED) {
      var addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'bpd-lb__add-edit';
      addBtn.textContent = isInEdit(p.id) ? 'Remove from your edit' : 'Add to your edit';
      addBtn.addEventListener('click', function () {
        toggleEdit(p.id);
        addBtn.textContent = isInEdit(p.id) ? 'Remove from your edit' : 'Add to your edit';
      });
      links.appendChild(addBtn);
    }
    if (p.hr) {
      var wed = document.createElement('a');
      wed.href = p.hr;
      wed.textContent = 'See her wedding';
      links.appendChild(wed);
    }
    bottom.appendChild(links);

    if (p.ph) {
      var credit = document.createElement('p');
      credit.className = 'bpd-lb__credit';
      credit.appendChild(document.createTextNode('Photography: '));
      // p.phw (photographer website) is a newer key and may be absent on
      // older data; p.pw is unrelated (boolean "product worn" — never
      // touch it here). Link the name only for an http(s) URL, built with
      // DOM APIs so a photographer name can never inject markup.
      if (p.phw && /^https?:\/\//i.test(p.phw)) {
        var credLink = document.createElement('a');
        credLink.href = p.phw;
        credLink.target = '_blank';
        credLink.rel = 'noopener noreferrer nofollow';
        credLink.textContent = p.ph;
        credit.appendChild(credLink);
      } else {
        credit.appendChild(document.createTextNode(p.ph));
      }
      bottom.appendChild(credit);
    }

    lbPanel.appendChild(bottom);
  }

  function paintLightboxImage(p) {
    lbImg.classList.remove('bpd-lb__img--in');
    lbImg.alt = p.a || '';
    lbImg.style.objectPosition = fpPos(p);
    var attrs = buildImgAttrs(p, 'hero');
    var next = new Image();
    next.onload = function () {
      lbImg.src = attrs.src;
      if (attrs.srcset) lbImg.srcset = attrs.srcset;
      requestAnimationFrame(function () { lbImg.classList.add('bpd-lb__img--in'); });
    };
    if (attrs.srcset) next.srcset = attrs.srcset;
    next.src = attrs.src;
  }

  function showLightbox(id, push) {
    var p = byId[id];
    if (!p || !lb) return;
    state.lightboxId = id;
    paintLightboxImage(p);
    renderLightboxPanel(p);
    lb.hidden = false;
    lb.classList.add('bpd-lb--open');
    requestAnimationFrame(function () { lb.classList.add('bpd-lb--in'); });
    document.documentElement.style.overflow = 'hidden';

    var url = currentUrl();
    url.searchParams.set('p', id);
    // `pushed` only ever becomes true via an actual pushState below — a
    // direct arrival at a `?p=` URL, a popstate-driven re-entry, or a
    // lightboxStep() replace all carry it forward as false/whatever the
    // current entry already has, so Close only ever calls history.back()
    // for an entry THIS page itself pushed onto the stack.
    if (push) {
      history.pushState({ bpdLightbox: true, bpdPushed: true }, '', url.toString());
    } else {
      var pushed = !!(history.state && history.state.bpdPushed);
      history.replaceState({ bpdLightbox: true, bpdPushed: pushed }, '', url.toString());
    }

    // Move focus into the dialog — but only if it isn't already there (a
    // next/prev step, or the arrow keys, shouldn't yank focus off whatever
    // control the visitor is already using inside it).
    if (!lb.contains(document.activeElement)) {
      var toFocus = lb.querySelector('.bpd-lb__close') || lb;
      if (toFocus && toFocus.focus) toFocus.focus();
    }
  }

  function openLightbox(id) {
    // The element that opened it (a grid tile) — Esc/close returns focus
    // here rather than leaving it stranded on a now-hidden dialog.
    state.lightboxOpener = document.activeElement || null;
    showLightbox(id, true);
    track('wall_open_photo', { photo_id: id, context: 'grid' });
  }

  function closeLightbox() {
    if (!lb.classList.contains('bpd-lb--open')) return;
    lb.classList.remove('bpd-lb--in');
    document.documentElement.style.overflow = '';
    state.lightboxId = null;
    setTimeout(function () { lb.classList.remove('bpd-lb--open'); lb.hidden = true; }, REDUCE_MOTION ? 0 : 240);
    var opener = state.lightboxOpener;
    state.lightboxOpener = null;
    if (opener && document.contains(opener) && opener.focus) opener.focus();
    // Only go back if THIS page pushed the history entry (opened a tile from
    // the grid). A direct arrival at a `?p=` URL replaces state instead —
    // going back would leave the gallery entirely (audit: "Direct-photo
    // close").
    if (history.state && history.state.bpdLightbox && history.state.bpdPushed) {
      history.back();
    } else {
      var url = currentUrl();
      url.searchParams.delete('p');
      history.replaceState(null, '', url.toString());
    }
  }

  // Keep Tab/Shift+Tab cycling within the open dialog — the panel's own
  // focusable elements change every render (product links, "Shop", "See her
  // wedding"...), so this reads the live DOM rather than a cached list.
  function lightboxFocusables() {
    if (!lb) return [];
    var items = lb.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])');
    return Array.prototype.filter.call(items, function (el) {
      return !el.disabled && el.offsetParent !== null;
    });
  }
  function trapLightboxFocus(e) {
    var items = lightboxFocusables();
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    var active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || !lb.contains(active)) { e.preventDefault(); last.focus(); }
    } else {
      if (active === last || !lb.contains(active)) { e.preventDefault(); first.focus(); }
    }
  }

  function lightboxStep(dir) {
    var list = orderedPhotos();
    var idx = list.findIndex(function (p) { return p.id === state.lightboxId; });
    if (idx < 0) return;
    var next = list[(idx + dir + list.length) % list.length];
    showLightbox(next.id, false);
  }

  if (lbClose) lbClose.addEventListener('click', closeLightbox);
  if (lbPrev) lbPrev.addEventListener('click', function () { lightboxStep(-1); });
  if (lbNext) lbNext.addEventListener('click', function () { lightboxStep(1); });
  if (lb) {
    lb.addEventListener('click', function (e) {
      if (e.target === lb || e.target === lb.querySelector('.bpd-lb__stage')) closeLightbox();
    });
  }
  document.addEventListener('keydown', function (e) {
    if (!state.lightboxId) return;
    if (e.key === 'Escape') closeLightbox();
    else if (e.key === 'ArrowRight') lightboxStep(1);
    else if (e.key === 'ArrowLeft') lightboxStep(-1);
    else if (e.key === 'Tab') trapLightboxFocus(e);
  });

  window.addEventListener('popstate', function () {
    var url = currentUrl();
    var p = url.searchParams.get('p');
    if (!p && state.lightboxId) {
      lb.classList.remove('bpd-lb--in');
      document.documentElement.style.overflow = '';
      state.lightboxId = null;
      setTimeout(function () { lb.classList.remove('bpd-lb--open'); lb.hidden = true; }, REDUCE_MOTION ? 0 : 240);
    } else if (p && p !== state.lightboxId && byId[p]) {
      showLightbox(p, false);
    }
  });

  // swipe (lightbox), same 1:1-feel threshold as the real-brides gallery
  if (lb) {
    var touch = { x: 0, active: false };
    lb.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      touch.x = e.touches[0].clientX;
      touch.active = true;
    }, { passive: true });
    lb.addEventListener('touchend', function (e) {
      if (!touch.active) return;
      touch.active = false;
      var dx = (e.changedTouches[0].clientX) - touch.x;
      if (Math.abs(dx) > 60) lightboxStep(dx < 0 ? 1 : -1);
    }, { passive: true });
  }

  // ---------------------------------------------------------------
  // Your edit — pill, tray, thumbs, veils, share link, email stub
  // ---------------------------------------------------------------
  var editPill = root.querySelector('[data-bpd-edit-toggle]');
  var editRoot = root.querySelector('.bpd-edit');
  var editScrim = editRoot ? editRoot.querySelector('.bpd-edit__scrim') : null;
  var editClose = editRoot ? editRoot.querySelector('.bpd-edit__close') : null;
  var editCount = editRoot ? editRoot.querySelector('.bpd-edit__count') : null;
  var editThumbs = editRoot ? editRoot.querySelector('.bpd-edit__thumbs') : null;
  var editVeilsList = editRoot ? editRoot.querySelector('.bpd-edit__veils-list') : null;
  var editVeilsBlock = editRoot ? editRoot.querySelector('.bpd-edit__veils') : null;
  var editShareBtn = editRoot ? editRoot.querySelector('.bpd-edit__share') : null;
  var editResetBtn = editRoot ? editRoot.querySelector('.bpd-edit__reset') : null;
  var editEmptyEl = editRoot ? editRoot.querySelector('.bpd-edit__empty') : null;
  var editFullEl = editRoot ? editRoot.querySelector('.bpd-edit__full') : null;
  var editEmailBlock = editRoot ? editRoot.querySelector('.bpd-edit__email') : null;
  var editEmailInput = editRoot ? editRoot.querySelector('.bpd-edit__email-input') : null;
  var editEmailSend = editRoot ? editRoot.querySelector('.bpd-edit__email-send') : null;
  var editSharedBlock = editRoot ? editRoot.querySelector('.bpd-edit__shared') : null;
  var editSharedLine = editRoot ? editRoot.querySelector('.bpd-edit__shared-line') : null;
  var editSharedAdd = editRoot ? editRoot.querySelector('.bpd-edit__shared-add') : null;

  function editedPhotos() {
    return editIds.map(function (id) { return byId[id]; }).filter(Boolean);
  }

  function weddingCount(photos) {
    var seen = {};
    var n = 0;
    photos.forEach(function (p) {
      var key = p.hr || p.id;
      if (!seen[key]) { seen[key] = true; n++; }
    });
    return n;
  }

  function uniqueVeils(photos) {
    var seen = {};
    var out = [];
    photos.forEach(function (p) {
      (p.pr || []).forEach(function (prod) {
        if (seen[prod.h]) return;
        seen[prod.h] = true;
        out.push(prod);
      });
    });
    return out;
  }

  var toastTimer = null;
  function showToast(msg) {
    var toast = root.querySelector('.bpd-toast');
    if (!toast) {
      toast = document.createElement('p');
      toast.className = 'bpd-toast';
      root.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('bpd-toast--in');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toast.classList.remove('bpd-toast--in'); }, 3200);
  }

  function showEditNotice(kind) {
    if (kind !== 'full') return;
    var msg = 'Your edit is full at ' + EDIT_MAX + ' photographs. Remove one to add another.';
    if (editRoot && editRoot.classList.contains('bpd-edit--open') && editFullEl) {
      editFullEl.hidden = false;
      setTimeout(function () { editFullEl.hidden = true; }, 3200);
    } else {
      showToast(msg);
    }
  }

  function updatePill() {
    if (!editPill) return;
    var n = editIds.length;
    editPill.hidden = n < 1;
    editPill.textContent = 'Your edit · ' + n;
  }

  function updateTileEditClasses() {
    if (!EDIT_ENABLED) return;
    Object.keys(tileEls).forEach(function (id) {
      tileEls[id].classList.toggle('bpd-tile--edit', isInEdit(id));
    });
  }

  function updateLightboxAddButton() {
    if (!state.lightboxId || !lbPanel) return;
    var addBtn = lbPanel.querySelector('.bpd-lb__add-edit');
    if (!addBtn) return;
    addBtn.textContent = isInEdit(state.lightboxId) ? 'Remove from your edit' : 'Add to your edit';
  }

  function renderEditTray() {
    if (!editRoot) return;
    var photos = editedPhotos();

    if (editCount) {
      editCount.textContent = photos.length + ' photograph' + (photos.length === 1 ? '' : 's') +
        ' · ' + weddingCount(photos) + ' wedding' + (weddingCount(photos) === 1 ? '' : 's');
    }

    if (editThumbs) {
      editThumbs.innerHTML = '';
      photos.forEach(function (p) {
        var thumb = document.createElement('div');
        thumb.className = 'bpd-edit__thumb';
        var img = document.createElement('img');
        var attrs = buildImgAttrs(p, 'tile1');
        img.src = attrs.src;
        img.alt = '';
        var thumbPos = fpPos(p);
        if (thumbPos) img.style.objectPosition = thumbPos;
        thumb.appendChild(img);
        var rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'bpd-edit__thumb-remove';
        rm.setAttribute('aria-label', 'Remove this photograph from your edit');
        rm.textContent = '✕';
        rm.addEventListener('click', function () { removeFromEdit(p.id); });
        thumb.appendChild(rm);
        editThumbs.appendChild(thumb);
      });
    }

    var veils = uniqueVeils(photos);
    if (editVeilsBlock) editVeilsBlock.hidden = veils.length < 1;
    if (editVeilsList) {
      editVeilsList.innerHTML = '';
      veils.forEach(function (v) {
        var row = document.createElement('div');
        row.className = 'bpd-edit__veil';
        var name = document.createElement('p');
        name.className = 'bpd-edit__veil-name';
        name.textContent = v.s || v.t;
        row.appendChild(name);
        var link = document.createElement('a');
        link.className = 'bpd-edit__veil-link';
        link.href = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root_url) || '').replace(/\/+$/, '') + '/products/' + v.h;
        link.textContent = 'Shop ' + v.t;
        row.appendChild(link);
        editVeilsList.appendChild(row);
      });
    }

    if (editEmptyEl) editEmptyEl.hidden = photos.length > 0;
    if (editShareBtn) editShareBtn.hidden = photos.length < 1;
    if (editResetBtn) editResetBtn.hidden = photos.length < 1;
    if (editEmailBlock) editEmailBlock.hidden = !EMAIL_ENABLED || photos.length < 1;
  }

  function onEditChanged() {
    updatePill();
    updateTileEditClasses();
    updateLightboxAddButton();
    if (editRoot && editRoot.classList.contains('bpd-edit--open')) renderEditTray();
  }

  function openEditTray() {
    if (!editRoot) return;
    renderEditTray();
    editRoot.hidden = false;
    editRoot.classList.add('bpd-edit--open');
    requestAnimationFrame(function () { editRoot.classList.add('bpd-edit--in'); });
  }

  function closeEditTray() {
    if (!editRoot) return;
    editRoot.classList.remove('bpd-edit--in');
    setTimeout(function () {
      editRoot.classList.remove('bpd-edit--open');
      editRoot.hidden = true;
    }, REDUCE_MOTION ? 0 : 300);
  }

  if (editPill) editPill.addEventListener('click', openEditTray);
  if (editClose) editClose.addEventListener('click', closeEditTray);
  if (editScrim) editScrim.addEventListener('click', closeEditTray);
  if (editResetBtn) editResetBtn.addEventListener('click', resetEdit);

  function shareUrl(ids) {
    var url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('edit', ids.join(','));
    return url.toString();
  }

  if (editShareBtn) {
    editShareBtn.addEventListener('click', function () {
      var url = shareUrl(editIds);
      var label = editShareBtn.textContent;
      if (navigator.share) {
        navigator.share({ title: 'The picture desk', url: url }).catch(function () {});
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function () {
          editShareBtn.textContent = 'Copied';
          setTimeout(function () { editShareBtn.textContent = label; }, 1800);
        }).catch(function () { log('could not copy share link'); });
      }
    });
  }

  // Email me my edit — stub only; wired to Klaviyo later.
  function sendEditEmail(email, ids, products) {
    log('sendEditEmail stub — email: ' + email + ', ids: ' + ids.join(',') +
      ', products: ' + products.map(function (p) { return p.h; }).join(','));
    // TODO: call the Klaviyo integration once it exists. Do not implement
    // a real send here.
  }

  if (editEmailSend) {
    editEmailSend.addEventListener('click', function () {
      if (!EMAIL_ENABLED) return;
      var email = editEmailInput ? editEmailInput.value : '';
      var photos = editedPhotos();
      sendEditEmail(email, editIds, uniqueVeils(photos));
    });
  }

  // Incoming shared edit (?edit=id1,id2,...) — never overwrites the
  // visitor's own edit silently; offers to add instead.
  function readSharedFromUrl() {
    var url = currentUrl();
    var raw = url.searchParams.get('edit');
    if (!raw) return null;
    var ids = raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    return ids.length ? ids : null;
  }

  function renderSharedBlock() {
    if (!editSharedBlock || !sharedIds) return;
    var known = sharedIds.filter(function (id) { return byId[id]; });
    if (editSharedLine) {
      editSharedLine.textContent = known.length ?
        (known.length + ' photograph' + (known.length === 1 ? '' : 's') + ' shared with you.') :
        'Loading the shared edit…';
    }
    editSharedBlock.hidden = false;
  }

  if (editSharedAdd) {
    editSharedAdd.addEventListener('click', function () {
      if (!sharedIds) return;
      var added = 0, blocked = false;
      sharedIds.forEach(function (id) {
        if (!byId[id]) return;
        if (editIds.length >= EDIT_MAX) { blocked = true; return; }
        if (!editSet[id]) { editIds.push(id); rebuildEditSet(); added++; }
      });
      if (added) saveEdit();
      sharedIds = null;
      if (editSharedBlock) editSharedBlock.hidden = true;
      onEditChanged();
      if (blocked) showEditNotice('full');
      // drop ?edit= now it's been offered/actioned, keep the rest of the URL
      var url = currentUrl();
      url.searchParams.delete('edit');
      history.replaceState(history.state, '', url.toString());
    });
  }

  function initEdit() {
    if (!EDIT_ENABLED) return;
    editIds = loadEdit().slice(0, EDIT_MAX);
    rebuildEditSet();
    updatePill();

    sharedIds = readSharedFromUrl();
    if (sharedIds) {
      openEditTray();
      renderSharedBlock();
    }
  }

  // ---------------------------------------------------------------
  // Your Britten (S1) — one-time legacy adapter. The dormant "Your edit"
  // (EDIT_ENABLED above, which stays false) kept photo ids in localStorage
  // under EDIT_STORAGE_KEY. Once the full archive is in (so every id can be
  // looked up) those photos are copied into the shared core as saved looks:
  // a look is the WEDDING, keyed by its slug (the last segment of the photo's
  // `hr`); the photo id rides along. Runs once (flag 'pictureDeskMigrated' in
  // the core's 'yb' slot), only when the Saved UI is on, and leaves the legacy
  // key exactly as it is. The flag is written BEFORE the saves: if the 'yb'
  // slot is not registered the write returns false and nothing happens, and a
  // later visit can never save again a look she has since removed.
  // ---------------------------------------------------------------
  var YB_MIGRATED = 'pictureDeskMigrated';

  function migrateLegacyEdit() {
    var core = window.BrittenCore;
    if (!core || !core.ready || !core.flags || !core.flags.on('yourBritten')) return;
    core.ready(function () {
      try {
        if (core.store.get('yb', YB_MIGRATED)) return;
        var ids = loadEdit();
        if (!ids.length) return;
        if (!core.store.set('yb', YB_MIGRATED, new Date().toISOString())) return;
        ids.forEach(function (id) {
          var p = byId[id];
          var slug = p && p.hr ? String(p.hr).split(/[?#]/)[0].replace(/\/+$/, '').split('/').pop() : '';
          if (!p || !slug) return;
          core.items.save({
            kind: 'look',
            look_slug: slug,
            look_archive_no: null,
            photo_id: id,
            title: p.b || null,
            image: p.u ? ((p.cdn || isShopifyCdn(p.u)) ? cdnUrl(p.u, 640) : p.u) : null
          });
        });
        log('legacy edit migrated: ' + ids.length + ' photo ids');
      } catch (e) {
        log('legacy edit migration failed: ' + e);
      }
    });
  }

  // ---------------------------------------------------------------
  // Opening — real-count headline (Turn 3: no hero photo). Painted
  // straight from the counts in the first batch, so it's correct
  // before the full archive has even started fetching.
  // ---------------------------------------------------------------
  function fmt(n) { return (n || 0).toLocaleString('en-GB'); }

  function paintHeadline(counts) {
    var h1 = root.querySelector('[data-bpd-headline]');
    if (!h1) return;
    var n = counts.n_photos, w = counts.n_weddings;
    if (!n) return;
    var tail = state.hasLenses ? 'sorted by colour and by moment.' : 'sorted by colour.';
    h1.textContent = fmt(n) + ' photograph' + (n === 1 ? '' : 's') + ' from ' + fmt(w) +
      ' real wedding' + (w === 1 ? '' : 's') + ', ' + tail;
  }

  // ---------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------
  function ingest(list, landings) {
    state.photos = list;
    list.forEach(function (p) { byId[p.id] = p; ensureColourCache(p); });
    if (landings && landings.length) state.landings = landings;
    updateHasLenses();
  }

  function boot(firstBatch) {
    ingest(firstBatch.photos || [], firstBatch.landings);
    initEdit();
    readColourFromUrl();
    readLensFromUrl();
    readSearchFromUrl();
    readLandingFromUrl();
    paintHeadline(firstBatch);
    renderSpectrum();
    renderLenses();
    updateViewSwitch();
    renderGrid();

    var initialP = currentUrl().searchParams.get('p');
    if (initialP) {
      // wait one frame so tiles exist to key off, but the photo may
      // not have loaded yet if it's outside the first batch — the
      // full-data fetch below will retry.
      if (byId[initialP]) showLightbox(initialP, false);
    }

    scheduleFullLoad();
  }

  // ---------------------------------------------------------------
  // Full archive load — the ~1MB raw / ~250KB gz JSON. Deferred to idle
  // time (or straight after window `load` where requestIdleCallback isn't
  // available) rather than fired at boot: this now loads on every landing
  // page too (mode: 'landing'), not just on the full archive, so it
  // must never compete with first paint there. The archive page still has
  // its own first-24-batch JSON (britten-picture-desk-data.liquid) to show
  // meanwhile; landing pages have their own server-rendered `.bwl-grid`
  // for the same job (see hideStaticSeoGridOnce).
  // ---------------------------------------------------------------
  function scheduleFullLoad() {
    var jsonUrl = root.getAttribute('data-json-url');
    if (!jsonUrl) return;
    function run() { loadFullArchive(jsonUrl); }
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 2000 });
    } else if (document.readyState === 'complete') {
      setTimeout(run, 0);
    } else {
      window.addEventListener('load', function () { setTimeout(run, 0); }, { once: true });
    }
  }

  function loadFullArchive(jsonUrl) {
    fetch(jsonUrl).then(function (r) { return r.json(); }).then(function (full) {
      state.fullLoaded = true;
      ingest(full.photos || [], full.landings);
      migrateLegacyEdit();
      if (LANDING_MODE && LOCKED_LANDING && landingByHandle(LOCKED_LANDING)) {
        setLanding(LOCKED_LANDING, { quiet: true });
      } else if (state._pendingLanding) {
        var pending = state._pendingLanding;
        state._pendingLanding = null;
        if (landingByHandle(pending)) setLanding(pending);
      }
      paintHeadline(full);
      renderSpectrum();
      renderLenses();
      renderLandingChip();
      updateViewSwitch();
      renderGrid();
      updateTileEditClasses();
      if (sharedIds && editRoot && editRoot.classList.contains('bpd-edit--open')) renderSharedBlock();
      var p = currentUrl().searchParams.get('p');
      if (p && byId[p] && state.lightboxId !== p) showLightbox(p, false);
      log('full archive loaded: ' + (full.photos || []).length + ' photos');
    }).catch(function (e) { log('archive fetch failed: ' + e); });
  }

  // ---------------------------------------------------------------
  // Sticky bar show/hide — reveal on scroll up, hide on scroll down,
  // only once the real control bar has scrolled out of view (tracked
  // by IntersectionObserver, not by reading layout every scroll tick).
  // The scroll handler itself does no layout reads at all: direction
  // comes from window.scrollY, and the one measurement it needs
  // (the theme's own sticky header height, so this bar sits below it)
  // is taken once at boot and again on resize, then cached.
  // ---------------------------------------------------------------
  var stickyEl = root.querySelector('[data-bpd-sticky]');
  var stickyHeaderOffset = 0;

  // Common theme sticky-header wrappers (desktop + mobile variants) —
  // measured only if actually positioned fixed/sticky right now.
  var STICKY_HEADER_SELECTORS = [
    '.header-sticky-wrapper.is-sticky',
    '#mobile-header-sticky-wrapper.is-sticky',
    '.header-sticky-wrapper',
    '#mobile-header-sticky-wrapper'
  ];

  function computeStickyHeaderOffset() {
    for (var i = 0; i < STICKY_HEADER_SELECTORS.length; i++) {
      var el = document.querySelector(STICKY_HEADER_SELECTORS[i]);
      if (!el) continue;
      var pos = window.getComputedStyle(el).position;
      if (pos !== 'fixed' && pos !== 'sticky') continue;
      var h = el.getBoundingClientRect().height;
      if (h > 0) return h;
    }
    return 0;
  }

  function initSticky() {
    if (!stickyEl) return;
    stickyHeaderOffset = computeStickyHeaderOffset();
    stickyEl.style.setProperty('--bpd-sticky-top', stickyHeaderOffset + 'px');

    var controlsBar = root.querySelector('.bpd-controls');
    var stickyEligible = false;
    var stickyVisible = false;
    var lastScrollY = window.pageYOffset;
    var scrollRaf = null;

    function setEligible(v) {
      if (v === stickyEligible) return;
      stickyEligible = v;
      stickyEl.classList.toggle('bpd-sticky--eligible', stickyEligible);
      stickyEl.setAttribute('aria-hidden', stickyEligible ? 'false' : 'true');
      if (!stickyEligible) setVisible(false);
      lastScrollY = window.pageYOffset;
    }

    function setVisible(v) {
      if (v === stickyVisible) return;
      stickyVisible = v;
      stickyEl.classList.toggle('bpd-sticky--visible', stickyVisible);
    }

    if (controlsBar && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        // Only go eligible once the bar has scrolled UP out of view —
        // a bar below the viewport (not yet reached) shouldn't count.
        setEligible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0 });
      io.observe(controlsBar);
    }

    window.addEventListener('scroll', function () {
      if (!stickyEligible || scrollRaf) return;
      scrollRaf = requestAnimationFrame(function () {
        scrollRaf = null;
        var y = window.pageYOffset;
        if (y > lastScrollY + 4) setVisible(false);
        else if (y < lastScrollY - 4) setVisible(true);
        lastScrollY = y;
      });
    }, { passive: true });
  }

  var firstScript = document.getElementById('bpd-first');
  var first = { n_photos: 0, n_weddings: 0, photos: [] };
  if (firstScript) {
    try { first = JSON.parse(firstScript.textContent); } catch (e) { log('bad bpd-first JSON: ' + e); }
  }

  var seo = root.querySelector('.bpd-seo');
  if (seo) seo.hidden = true;

  var resizeRaf = null;
  window.addEventListener('resize', function () {
    if (resizeRaf) return;
    resizeRaf = requestAnimationFrame(function () {
      resizeRaf = null;
      renderGrid();
      if (stickyEl) {
        stickyHeaderOffset = computeStickyHeaderOffset();
        stickyEl.style.setProperty('--bpd-sticky-top', stickyHeaderOffset + 'px');
      }
    });
  });

  initGapsToggle();
  initSticky();
  boot(first);
})();
